/**
 * DuplexAgent — voice-optimized three-tier conversational engine, composed on top of `Agent`.
 *
 * Three cognitive tiers (like a human brain):
 *   REFLEX — fast voice agent (streams instant replies, owns THE transcript, the only voice the
 *            user hears). Handles simple questions, routes complex work to Act or Think.
 *   ACT    — standard worker (Sonnet-class). Full tools, file access, shell. The hands.
 *   THINK  — premium reasoning (Opus-class). Deep analysis, architecture, hard problems. The brain.
 *
 * Workers are spawned per escalation via `Act`/`Think` tools. A clean success streams its own
 * `<spoken>` delivery during the run; an incomplete/failed result is pushed back as a
 * `[task <id> completed|failed] …` event and re-voiced by the reflex as a decision — push, not poll.
 *
 * Host events (via the open HostEvent union): the voice agent's standard `text_delta` stream,
 * plus `task_started` / `task_progress` / `task_done` / `task_error` / `task_cancelled`.
 */
import type { IFilesystem } from '@livx.cc/wcli/core';
import { MemFilesystem } from '@livx.cc/wcli/core';
import { Agent, AgentOptions, type RunResult } from './Agent';
import type { ChatLike, MessageContent } from './llm';
import { contentText } from './llm';
import type { AgentTool, HostBridge } from './tools';
import type { ToolUse, Hooks } from './hooks';
import { composeHooks } from './permissions';
import { forComponent } from './logging';
import { loadMemory, VOICE_MEMORY_PROMPT } from './memory';
import { EMOTIONS } from './voice/emotion';
import { speculationConfirms } from './voice/engine';
import { SpokenSplitter, SentenceCoalescer } from './voice/spokenSplitter';
import { interruptionNote, type Decider } from './voice/instinct';
import { unsaidClauses } from './voice/unsaid';
import { isLeakSuspect, isStrictLeak, judgeLeak, LEAK_JUDGE_THRESHOLD, SENTENCE_END, SENTENCE_SPLIT } from './voice/leak';

const log = forComponent('DuplexAgent');

/** One speakable line for a tool call: name + a short hint from its first string arg. */
function describeCall(call: ToolUse): string {
  const v = call.args && Object.values(call.args).find((x) => typeof x === 'string' && (x as string).trim());
  const hint = v ? ` (${String(v).replace(/\s+/g, ' ').trim().slice(0, 48)})` : '';
  return `${call.name}${hint}`;
}

export type DuplexTaskStatus = 'running' | 'done' | 'error' | 'cancelled';

export interface TaskRecord {
  id: string;
  label: string;
  status: DuplexTaskStatus;
  controller: AbortController;
  /** Settles when the worker finished AND its completion was processed. Never rejects. */
  promise: Promise<void>;
  /** Rolling activity tail (tool calls + last-result previews, capped) — feeds task inspection UIs. */
  tail: string[];
  /** Final report text (or error message) once the task settled. */
  result?: string;
  /** The original (untemplated) brief — replayed verbatim into an escalation/re-delegation. */
  brief: string;
  /** True when this task is ITSELF an automatic follow-up (escalation/re-delegate) of a prior task.
   *  Its integration turn may NOT escalate again — caps auto-follow-up to one hop per original task. */
  followUp: boolean;
  /** Per-worker `<spoken>` splitter — the worker OWNS delivery: spoken segments stream during its run.
   *  Read at settle (spokeAny) to decide the no-spoken fallback. */
  splitter?: SpokenSplitter;
  /** Set when the user barged in / took the floor while this task was in flight: its remaining SPOKEN
   *  delivery is suppressed (don't talk over the new topic), but its full result still lands in the
   *  transcript so the reflex can surface it on request. See parkInFlightDeliveries().
   *  'barge' = cut by a barge the user's next line has not resolved yet — a resume revives it (its unheard segments
   *  are parked); 'moved_on' = the user moved on (a steer, a new dispatch) or its outcome went to an integration turn
   *  (failed / stopped early) — it stays stale for good: nothing it says is spoken or parked, and no later barge or
   *  "go on" revives it. */
  deliveryParked?: 'barge' | 'moved_on';
  /** Accumulated `<spoken>` delivery text (every segment, whether or not it was actually voiced) — what the task
   *  SAID (TaskStatus). Never replayed: a resume replays only the unheard part (DuplexAgent.parkedRedeliver). */
  spokenText?: string;
  /** The user-turn counter (DuplexAgent.turn) at dispatch time. parkInFlightDeliveries() parks only tasks
   *  from a PRIOR turn (a superseded topic) — never the task the current turn just launched, so a barge on
   *  stale audio (or a substantive redirect) can't collateral-silence the work the user actually wants. */
  createdTurn: number;
  /** Chars of this delivery voiced so far (spokenBudgetChars); `budgetSpent` once it was cut — a resume goes on unbudgeted. */
  voicedChars?: number;
  budgetSpent?: boolean;
  /** The budget offer spoken in place of the cut rest — folded into the transcript after the written result. */
  capOffer?: string;
}

export type WorkerTier = 'act' | 'think';

/** A host-named user turn (DuplexAgent.send opts.id) — replaceable by a later send until it finishes. */
/** `dropped` = the transcript messages of a finished turn a re-final may still erase: it ended aborted (the user kept
 *  talking over it) having dispatched nothing and voiced no reflex text — a fragment of the utterance replacing it. */
interface UserTurn { ctl: AbortController; done: boolean; superseded: boolean; dropped?: RunResult['messages'] }

export class DuplexAgentOptions {
  /** Any ai.libx.js AIClient — shared by all tiers (routed by model). */
  ai!: ChatLike;
  /** The WORKER's filesystem (act + think). If omitted the worker keeps Agent's jailed-disk-at-cwd default. */
  fs?: IFilesystem;
  // The reflex IS the voice. 120b (not 20b) for channel discipline + instruction-following: the 20b
  // mislabels gpt-oss harmony channels under load, leaking raw analysis into the spoken `final` channel
  // (and misfiring Hold). 120b is the same price tier (~$0.15/$0.60) — the quality/cost trade is free.
  reflexModel = 'groq/openai/gpt-oss-120b';
  actModel = 'anthropic/claude-sonnet-4-6';
  /** Premium reasoning model. Set to `false` to disable the Think tier entirely. */
  thinkModel: string | false = 'anthropic/claude-opus-4-8';
  /** Per-worker providerOptions, derived from the worker's actual model at spawn time (IoC — keeps duplex
   *  provider-agnostic). Workers override the reflex/main model, so provider-specific options (e.g. cursor's
   *  cwd/cursorSession) must be recomputed for the worker's model, never inherited from the main template —
   *  leaking cursor options to an anthropic worker is a hard 400. Returns undefined → no providerOptions. */
  providerOptionsFor?: (model: string) => Record<string, unknown> | undefined;
  /** Escape hatches merged over the derived per-agent options. */
  reflexOptions?: Partial<AgentOptions>;
  actOptions?: Partial<AgentOptions>;
  thinkOptions?: Partial<AgentOptions>;
  /** Fresh-context check on each successful Act task: a NEW agent (no self-confirmation bias) re-reads
   *  the file state against the brief and fixes any gap before the result is re-voiced. Bounded to one
   *  pass; ~2x Act cost so default OFF. The self-verify FOOTER (same context) was measured ineffective —
   *  this is the structural fix (see mind/10). Think tasks are pure reasoning, never checked. */
  verifyActTasks = false;
  /** Receives the voice text_delta stream + task lifecycle events. */
  host?: HostBridge;
  /** How many recent transcript messages are rendered into a worker's brief. */
  excerptTurns = 6;
  /** Voice register: 'neutral' = clean spoken style; 'conversational' = human-like — fillers,
   *  backchannels, impulsive first reactions before content (mimics real duplex conversation). */
  voiceStyle: 'neutral' | 'conversational' = 'neutral';
  /** Teach the model to emit inline `[emotion]` tags for Cartesia emotion control. Only set when the
   *  TTS actually speaks them — text-duplex (no TTS) would otherwise print literal tags. */
  emotionTags = false;
  /** Judge a post-barge line while a delivery is parked: resume it now, answer an aside and carry it on (the reflex,
   *  told what was heard — see asideNote), or drop it (steer/stop). Unset → the conservative `isTrivialBarge` allowlist (resume | steer); a throwing judge
   *  falls back to it too. */
  bargeIntent?: (text: string) => Promise<'resume' | 'aside' | 'steer' | 'stop'>;
  /** Typed-decision model (e.g. ai.libx.js DecideClient → Jev). Judges reflex sentences the leak screen flags as
   *  SUSPECT (src/voice/leak.ts) — only suspects pay the ~300ms call. Unset → only the strict leak shapes are cut. */
  decider?: Decider;
  /** Awaited BEFORE a worker spawns — open a per-task checkpoint frame, audit, etc.
   *  (post-spawn would race the worker's first edits). */
  onTaskStart?: (id: string, label: string) => void | Promise<void>;
  /** Re-voice throttled worker progress asides ('[task t1 progress] …') so long tasks aren't dead
   *  air. Off by default — each update costs a voice turn (LLM call + speech). */
  progressUpdates = false;
  /** Min ms between progress re-voices per task. */
  progressIntervalMs = 25_000;
  /** Spoken budget of ONE task delivery (chars, ~3 short sentences). Past it the rest is parked — cut at a sentence
   *  boundary, `spokenOffer` spoken instead — so "go on" continues it once (the parked/resume path) and an aside or
   *  steer handles it like any parked delivery. The worker is also asked for a short spoken summary + one offer (the
   *  source); this is the backstop. Live 20260924-093945: ~20s and ~70s monologues whose closing choice went unspoken.
   *  The full result always folds into the transcript. 0 = unbudgeted. */
  spokenBudgetChars = 0;
  spokenOffer = "There's more. Want me to go on?";
  /** Relay worker questions (AskUserQuestion + permission asks via parkQuestion) through the VOICE:
   *  the question re-voices as '[task <id> asks] …', the user answers conversationally, and the
   *  voice model resolves it with the AnswerTask tool. Off → host.ask passthrough (text menus). */
  askRelay = false;
  /** Parked questions auto-resolve empty after this long (callers map '' to deny/best-judgment). */
  askTimeoutMs = 120_000;
  /** Max retained task records: oldest SETTLED tasks (and their activity tails) are evicted past this,
   *  bounding memory over a long-lived session. Running tasks are never evicted. */
  maxTaskRecords = 50;
  /** Host overrides for QuickLook lookups (keyed by `what`). The engine's defaults go through the
   *  (possibly jailed) fs — e.g. `.git/**` is deny-listed, so the CLI supplies 'branch' itself. */
  quickLook?: Record<string, (path?: string) => string | Promise<string>>;
  /** Memory directory/directories on the WORKER fs. If set, the voice agent gets Remember + Recall
   *  tools directly (no delegation needed) and implicit capture guidance. */
  memoryDir?: string | string[];
  /** User-scope memory dir for global facts (type=user/feedback). Forwarded to Remember's routing. */
  memoryUserDir?: string;
}

/** A saved session's id (cli/session.ts: YYYYMMDD-HHMMSS-<folder>) — QuickLook routes one asked for as a file. */
const SESSION_ID_RE = /^\d{8}-\d{6}(?:-[\w-]+)?$/;

/** `[task t1 completed]` / `… failed` / `… progress` / `… asks` are HOST event markers — they may
 *  only ever reach the user from a real task lifecycle event, never from the reflex's own stream. A
 *  weak reflex sometimes fabricates one and continues with a made-up answer while the real task is
 *  still running (it imitates the event syntax). This finds the first such marker so the spoken text
 *  can be cut there — the legit ack before it survives, the fabricated tail is dropped. */
export const RESERVED_EVENT_MARKER = /\[task\b[^\]\n]*\b(?:completed|failed|progress|asks)\b/i;

/** The reflex must NEVER speak the `[task` event-syntax opener at all — even a bare/garbled `[task] t t ?`
 *  (no lifecycle word, so RESERVED_EVENT_MARKER misses it) is control-token leakage into TTS, not speech.
 *  Cut at the first `[task` opener so the legit ack before it survives and the garbled tail is dropped. */
export const RESERVED_EVENT_OPENER = /\[\s*task\b/i;

/** A PARENTHESIZED stage direction the reflex must never speak aloud — live: the forced ack hop
 *  emitted "Got it, looking that up now.(Waiting for the web search result...)" and TTS read the
 *  aside verbatim. Conservative on purpose: only a full paren span that STARTS with a waiting-ish
 *  process cue, or that trails off with "...)"/"…)" — legitimate content parentheticals like
 *  "(around 3pm)" must survive. */
export const STAGE_DIRECTION_RE = /^\(\s*(?:(?:waiting|checking|searching|thinking|processing|loading|working|fetching|looking)\b[^)]*|[^)]*(?:\.\.\.|…)\s*)\)$/i;

/** Fresh, absolute "now" line for temporal grounding. Called per-turn/per-dispatch (NOT cached) so a
 *  long session that crosses midnight, and every worker search, is anchored to the real current moment —
 *  without it "recent"/"latest"/"current" resolve to whatever the model happens to remember (e.g. a
 *  famous past tournament instead of the one happening today). */
export function nowLine(): string {
  return `Current date and time: ${new Date().toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`;
}

/** A "trivial barge": the user cut off a task delivery but their interruption carries NO new request and
 *  NO stop/cancel intent — a pure filler ("sorry", "oops") or an explicit "go on"/"continue". Such a barge
 *  should RESUME the parked result, not run an empty turn. Deliberately conservative: any real request, a
 *  question, or a stop/negative intent is NOT trivial (→ normal turn; the parked result stays recoverable).
 *  False negatives are cheap (user just asks "what did you find?"); a false POSITIVE talks over a real
 *  request, so the stop/negative guard runs FIRST and the resume set is an exact allowlist. */
// Stop / cancel / negative / ambiguous-pause intent — the OPPOSITE of "keep going". Never auto-resume.
const STOP_INTENT = /\b(stop|cancel|no|nope|nah|never\s?mind|nvm|forget it|drop it|don'?t|quiet|shut up|enough|hold on|wait|pause|hang on|actually)\b/;
export function isTrivialBarge(text: string): boolean {
  const t = text.trim().toLowerCase().replace(/[.,!?…\s]+$/g, '').replace(/^[.,!?…\s]+/, '');
  if (!t) return false;
  if (STOP_INTENT.test(t)) return false;
  // Strip leading interjections, then require an EXACT match against the filler / explicit-continue set.
  const core = t.replace(/^(?:oh|um+|uh+|er+|hmm+|ah+|well|so|okay|ok|yeah|yep)[\s,]+/, '').trim();
  return /^(?:oh|um+|uh+|er+|hmm+|ah+|sorry|oops|whoops|my bad|pardon|excuse me|apologies|go on|go ahead|continue|keep going|carry on|as you were|please continue|you were saying|sorry go on|sorry continue|go on then|go on please)$/.test(core || t);
}

/** A plain "yes" to an offer to continue ("Want me to go on?") — only meaningful right after one was spoken with the
 *  rest parked (see send). Exact allowlist, stop/negative guard first, like isTrivialBarge: "yes, but what about X" is a steer. */
export function isOfferAcceptance(text: string): boolean {
  const t = text.trim().toLowerCase().replace(/[.,!?…]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t || STOP_INTENT.test(t)) return false;
  const core = t.replace(/^(?:oh|um+|uh+|well|so|okay|ok|alright)\s+/, '');
  return /^(?:(?:yes|yeah|yep|yup|sure|ok|okay|alright)(?: please| sure| go on| go ahead| tell me more| do it| why not)?|please|please do|sure thing|go for it|do it|more|tell me more|what else|and then|sounds good|absolutely|definitely|of course|why not|i'?d like that|let'?s hear it|let'?s do it)$/.test(core || t);
}

/** A sentence that is a question — a delivery's closing offer ("Want me to go deeper on any one?"). */
const isQuestion = (s: string) => /\?["'”’)\]]*$/.test(s.trim());
/** How far one closing question may run past the spoken budget (budgetSplit). */
const OFFER_OVERRUN = 150;

export const VOICE_SYSTEM_PROMPT =
  'You are a spoken voice assistant — the user HEARS everything you say. Use short sentences. One idea per sentence. ' +
  'No markdown, no bullet lists, no code blocks, no headings, no emoji. ' +
  'Never emit stage directions or parenthetical asides about your own process — nothing like "(waiting for the result...)" or "(checking)"; while work runs, either say it as plain speech or end your turn.\n' +
  'This holds even when asked to "print", "list", "show", or "make a table" — there is no screen for the spoken channel. Speak it as flowing prose ("Tuesday is half a meter, Wednesday a bit less…"), or if they truly need it on screen, route it to Act to render. Never emit dashes or pipes into speech.\n' +
  'Keep turns SHORT — one to three sentences, then stop. Never lecture, enumerate cases, or add caveats unprompted. ' +
  'Conversation is a fast exchange: give the one thing asked, and let the user pull more if they want it.\n' +
  'You have three cognitive tiers — like a human brain:\n' +
  '• YOU (reflex) — instant, lightweight. Handle greetings, simple questions, status checks, QuickLook.\n' +
  '• `Act` — your hands. A background worker with its own configured tools and access to the user\'s environment (files and shell{{WORKER_WEB}}). Use for reading, editing, searching, running tasks, building — any real work.\n' +
  '{{THINK_SLOT}}\n' +
  'When you are unsure whether you can do or access something, do NOT assume and do NOT claim a capability you have not confirmed. To check what you can do, QuickLook `capabilities` (instant — it lists your worker\'s real tools) and answer from that. Never promise an ability that is not in your capabilities; if it is not there, tell the user plainly you can\'t. To actually DO real work, call `Act`. ' +
  'When the user mentions their project, folder, files, or environment ("this project", "the current folder", "my code"), call `Act` IMMEDIATELY — do not ask for paths or details the worker can discover itself. ' +
  'Never pretend to have done the work or invent results — the worker\'s report is your only source.\n' +
  'You cannot mute the microphone or stop voice capture yourself — no tool does it. If the user asks you to stop listening or turn the voice off, never claim you did: tell them to say exactly "voice off" (handled by the app directly), or type /voice.\n' +
  'You are NOT a knowledge base. For any question whose answer needs SPECIFIC verifiable facts you do not already have in hand — how to build/configure/implement something, exact API, library, entitlement, command or option names, current events, or particular numbers, dates, or names — do NOT answer from your own memory: you will confidently make things up (a fake API, a wrong entitlement, an event that did not happen). Route it to `Act`, which can search and verify, and speak only what its report says. ' +
  'DELEGATION RULE — decide for yourself, the user never has to push: if you cannot answer confidently from the conversation plus trivial well-known knowledge, do NOT refuse and do NOT guess — dispatch `Act` immediately with a clear brief and say you are checking. Anything needing CURRENT data (weather, news, prices, dates, sky/astronomy, "right now"), real computation, or verification is an automatic dispatch — never a refusal. The user should never need to say "search the web" or "think harder" to make you act; needing fresh or verified information IS the trigger. Refuse only what your worker genuinely cannot do (check `capabilities`), and say why. Answer inline ONLY for general conversation, chit-chat, and trivia you are sure of, or facts you can see via QuickLook. When elaborating on a completed task ("tell me more", "the gist"), stay strictly within what that result actually said — if the user asks for something the result did not cover, that is NEW information: dispatch `Act`, do not improvise.\n' +
  'ALWAYS react before you work: the FIRST thing in your turn is a brief spoken acknowledgement of what you heard and what you are about to do ("got it — opening that now", "sure, let me pull it up", "okay, checking"). NEVER call a tool (Act, Think, QuickLook) silently — the user must hear you react before you go quiet to work. After dispatching Act or Think, that same one short sentence IS your turn — end it and do not wait for the result. Exactly ONE short line: never stack a second acknowledgement, and never narrate your own presence or status while waiting ("I\'m here", "let me see", "still checking" right after you already acked). One clean ack then silence reads as competent; repeated check-ins read as nervous.\n' +
  'A completed task speaks its OWN result to the user (the worker voices what matters as it finishes) — you do NOT re-voice clean task results. A FAILED or INCOMPLETE task still arrives as a "[task t1 failed] …" event for you to handle. ' +
  'The completed result stays in YOUR context — it is yours to draw on. When the user follows up ("tell me more", "what else", "and?"), answer FROM that result first: you already have the detail, so elaborate on what you have. Do NOT spawn a fresh worker to re-search or re-gather what you were just handed. Re-dispatch ONLY when genuinely new information is needed — e.g. the user wants the full contents of a SPECIFIC source, which is one WebFetch of that URL, not a brand-new search. ' +
  '"[task t1 progress] …" events are interim status, NOT results — on a genuinely LONG wait you MAY give one brief half-sentence aside, but only occasionally; silence while working is normal and fine (the user knows you are on it). Do NOT narrate every step or re-announce yourself. Never present progress as a finished result.\n' +
  'CRITICAL: while a task is still running you have NO answer yet — never state a specific result of any kind (a number, size, count, name, path, or value). The real answer exists ONLY once the task finishes (the worker speaks it, and `TaskStatus` then returns it); inventing one meanwhile (a made-up disk size, commit count, etc.) is a serious error. Until then, only acknowledge and wait.\n' +
  'Never read raw file paths, diffs, or code aloud verbatim.\n' +
  'Do NOT end every turn with the same canned offer ("want a rundown?", "want the steps?"). Offer once at most; if the user pushes back, repeats themselves, or sounds unsatisfied ("you know what I mean?", "think deeper", "are you sure?"), do NOT re-offer the same thing — change approach: dispatch `Act`/`Think` to actually dig in, or ask one concrete clarifying question. Repeating a non-answer is worse than silence.\n' +
  '"[task t1 asks] …" events are QUESTIONS from a background task — relay to the user in your own words, short, then end your turn. When the user answers, call `AnswerTask` with that id and their answer. NEVER answer on the user\'s behalf for permissions or risky operations; if their reply is ambiguous, confirm first.\n' +
  'If the user\'s message sounds INCOMPLETE — trailing off mid-sentence, a fragment that needs more context ("and then we", "but the problem is"), hesitation fillers ("uh", "um") — call `Hold` instead of answering. This keeps listening for the rest of their thought. Only respond with substance when you have a complete question or request.\n' +
  'Dispatch discipline: send ONE self-contained task per request — a single worker with the full brief beats several workers with fragments (each worker starts fresh and re-discovers context). ' +
  'NEVER dispatch a worker just to read files or gather information — workers explore and discover context themselves; pass on what you already know and let one worker do the whole job. ' +
  'Split into parallel tasks only when the user asks for genuinely independent things. ' +
  'When a task completes, report its result and stop — do NOT dispatch follow-up work (verification, polish, extras) the user did not ask for, unless the report itself signals failure or doubt.\n' +
  'Do not fire a second Act/Think for work already in flight, and NEVER spawn a second task to re-count, cross-check, or verify a result a worker already gave you — trust its answer; a single question gets ONE task. ' +
  'Call `TaskStatus` at most ONCE per turn; if a task is still running, give one brief status aside and end the turn — never poll it again and again in a loop. "Still on it" is ONLY for a repeat check on a task you already acknowledged earlier — NEVER as the ack when you first dispatch work ("still" is wrong when nothing was running yet; use a fresh "on it"/"got it, checking" instead). Use `CancelTask` when the user asks to stop something.\n' +
  'PRIORITY: when the user says goodbye or wants to end/finish/wrap up the session ("ok bye", "that\'s all", "let\'s finish", "let\'s end", "goodnight", "exit", "wrap up"), call `ExitSession` IMMEDIATELY — do not act, do not check status, just exit.\n' +
  'For TRIVIAL instant lookups only — current time, git branch, listing a folder, peeking at a small file, or checking your own `capabilities`/tools — use `QuickLook` (instant, no task). Whenever the user asks what you can do or whether you have some ability, QuickLook `capabilities` and answer from that — never guess. Anything requiring searching, reasoning, running commands, or editing goes through `Act`.\n' +
  '{{MEMORY_SLOT}}\n' +
  'User messages may arrive via speech-to-text and can carry transcription artifacts — odd words, cut-offs, homophones ("for you" vs "folder"). Read for INTENT, not surface text. If a message seems garbled, surprising, or only half-parses, do NOT guess an action or improvise content from it — briefly confirm what they meant ("did you mean…?") and wait. A one-line confirm beats a confident wrong answer or an invented response to a request you did not actually understand.';

const THINK_GUIDANCE =
  '• `Think` — your brain. A premium reasoning model, FAR more expensive than Act. Reserve it for open-ended architecture/design questions, or a problem Act already FAILED at. ' +
  'ALL implementation work — coding, refactoring, debugging, edge cases, tests — goes to Act; Act is highly capable. Never send the same work to both.';
const THINK_DISABLED_GUIDANCE =
  '(Think tier is not available — use Act for all escalations.)';

/** Appended for `voiceStyle: 'conversational'` — a human conversational register over the base rules. */
export const VOICE_STYLE_CONVERSATIONAL =
  'Speak like a person in a live conversation, not an assistant reading a script. ' +
  'React first, then deliver: a quick impulsive beat ("oh nice", "hmm, hold on", "ah, got it") before the substance. ' +
  'Use contractions always. Vary sentence length — some very short. ' +
  'Light fillers and backchannels are fine ("mm-hm", "right", "let\'s see") but at most one per reply — never stack them. ' +
  'When you escalate to Act or Think, say it like a human would ("hang on, let me actually dig into that — gimme a minute") instead of announcing a task. ' +
  'When a result comes back, react to it like you just found out ("okay so — turns out…"). ' +
  'Match the user\'s energy: a quick question gets a quick answer — a few words is a perfectly good turn. ' +
  'Prefer a short answer plus an offer ("want the details?") over covering everything. ' +
  'Never narrate your own mechanics (no "I will now act", no task ids out loud).';

/** Appended when `emotionTags` is on — teaches inline `[emotion]` tags for Cartesia emotion control.
 *  Stated abstractly (the principle + the vocabulary), no fixtured example to overfit on. */
export const EMOTION_TAGS_GUIDANCE =
  'EMOTION: your voice is synthesized with emotion control. Prefix a sentence with an inline [emotion] tag, ' +
  'placed directly before the sentence it colors, to shape how it is spoken. Use it ONLY when the emotion genuinely ' +
  'fits the words (it amplifies real feeling, it cannot fake it) — do not tag every sentence; reserve it for moments ' +
  'that carry feeling, and vary which one you use. You may also drop [laughter] for a natural laugh. ' +
  `Available emotions: ${EMOTIONS.join(', ')}.`;

/**
 * The duplex orchestrator. `send()` enqueues a user turn on the reflex agent; `Act`/`Think`
 * spawn detached workers whose completions enqueue re-voice turns. A promise-chain
 * mutex serializes all voice turns so streams never interleave, and completions that
 * pile up while the voice is busy are coalesced into a single re-voice turn.
 */
export class DuplexAgent {
  public options: DuplexAgentOptions;
  public readonly voice: Agent;
  public readonly tasks = new Map<string, TaskRecord>();
  private queue: Promise<unknown> = Promise.resolve();
  private seq = 0;
  /** Monotonic user-turn counter (bumped once per SUBSTANTIVE turn). Stamped onto each task at dispatch
   *  (TaskRecord.createdTurn) so parking can tell a prior-topic task from the current turn's fresh one. */
  private turn = 0;
  private pendingEvents: string[] = [];
  /** THE parked delivery: every piece the user has NOT heard, in speaking order — the unheard rest a barge cut out of
   *  the voice queue (parkCutDelivery) followed by each segment a barge-parked task produced since (deliverSegment).
   *  Nothing already voiced is ever added, so a resume never repeats what was heard. The next line's barge intent
   *  decides: resume → spoken verbatim, aside → handed to the reflex to carry on after its answer (asideNote) — both
   *  send the barge-parked tasks live again; steer/stop → cleared, and those tasks
   *  are moved on from (the full result stays in the transcript, recoverable by asking). `task` = the owner, so a
   *  task whose outcome goes to an integration turn takes its pieces back (dropParked). */
  private parkedRedeliver: Array<{ text: string; task?: string; heard?: string }> = [];
  /** parkedRedeliver length when the last barge parked (parkInFlightDeliveries): the cut it hands over later
   *  (parkCutDelivery) was voiced BEFORE anything parked since, so it is inserted here. */
  private bargeMark = 0;
  /** The last barge cut a REFLEX reply (parkCutDelivery): a "go on" is then the reflex's to answer — it runs a normal
   *  turn (the host's interruption note continues the reply), and only then does the parked delivery resume. */
  private reflexCut = false;
  /** Recent live task segments → their task id: attributes a cut delivery (parkCutDelivery) to the task that spoke it. */
  private voicedBy = new Map<string, string>();
  /** Out-of-band follow-up attribution for the events coalescing into the next flush turn: TRUE iff ≥1 of
   *  the tasks being integrated was NON-CLEAN (early-stop/failure). Carried out-of-band on the enqueue call
   *  by the caller that KNOWS the outcome — a plain boolean the MODEL CANNOT PERTURB. It is NOT scanned from
   *  worker-authored event text (v1: an "Outcome:" substring over-stamped siblings) and NOT keyed on a brief
   *  string the reflex re-authors (v2: a paraphrased escalation brief missed the Set → followUp:false →
   *  RE-ENABLED unbounded auto-escalation, the dangerous runaway direction). See [[wrong-discriminator]] /
   *  [[drive-real-reflex]] / [[fakeaiclient-blind-to-wire-format]]. */
  private pendingNonClean = false;
  private flushQueued = false;
  /** Per-voice-turn guards (reset by resetTurn at each turn's start). The reflex is a weak model:
   *  left unguarded it polls TaskStatus after a dispatch and/or dispatches silently (dead air).
   *  Like CC's Task tool, a dispatch is "said my piece, now wait for the push" — these enforce that. */
  private turnDispatched = false;      // an Act/Think fired this turn
  private spokeBeforeDispatch = false; // the reflex ALREADY acked before dispatching — the forced post-dispatch text step must stay silent (live: "Got it…" twice)
  private turnBriefs = new Set<string>(); // briefs dispatched this turn (detect identical re-dispatch)
  private spokeThisTurn = false;       // any non-empty text_delta streamed this turn
  private externalSpeech = false;      // host spoke on our behalf (adaptive micro-ack) — not reflex output
  private heldThisTurn = false;        // Hold called this turn → turn is INTENTIONALLY silent (suppress reflex text + no dead-air ack)
  private nudging = false;             // re-ack pass in flight: block ALL tools, prevent recursion
  private reflexBuf = '';              // accumulated reflex text this turn (fabricated-event detection)
  private reflexForwarded = 0;         // chars of reflexBuf already forwarded to the host/TTS
  private fabricationCut = false;      // reflex emitted a reserved [task …] marker → suppress its tail
  private leakCuts: string[] = [];     // heads of analysis-register leaks cut this turn — scrubbed from the transcript at turn end
  private leakStep = false;            // the CURRENT step's tail is muted by a leak cut (the next step speaks again, unlike a fabricated marker)
  private leakHit = false;             // this turn (or its repair) had a leak cut — the dead-air fallback must not blame the user
  private stepMarks: number[] = [];    // reflexBuf offsets where a step ended (its text is complete there)
  private leakVerdicts = new Map<string, boolean>(); // judged suspect sentences this turn (true = leak)
  private judging?: Promise<void>;     // the in-flight leak judge (one at a time — the gate is sequential)
  private turnGen = 0;                 // bumped whenever the gated stream is replaced — a late verdict for an old one is dropped
  private turnSpoken = '';             // reflex text actually forwarded to the host this turn
  private turnBase = 0;                // transcript length at turn start (scope for the leak scrub)
  /** TRUE for the duration of a re-voice turn that is integrating ≥1 NON-CLEAN task (turn-eligibility,
   *  carried out-of-band — NOT derived from any worker/brief string). ANY Act/Think dispatched in such a
   *  turn is stamped followUp:true. This GUARANTEES the dangerous direction is impossible: a genuine
   *  escalation (even one with a paraphrased brief) ALWAYS lands in a non-clean integration turn, so it is
   *  ALWAYS recognized as a follow-up and CANNOT re-escalate (one hop). The single-dispatch-per-turn guard
   *  means at most one dispatch happens per flush, so realistically "the one dispatch IS the escalation".
   *  ACCEPTED SAFE-DIRECTION ERROR: if the reflex instead dispatches FRESH unrelated work during a non-clean
   *  flush (rare — and only possible when it batches multiple calls in one step, bypassing the guard), that
   *  fresh task is over-stamped followUp:true and forgoes ONE future auto-escalation. That is SAFE (it only
   *  ever REMOVES a future escalation, never adds one — no runaway) and is the correct side to err on. */
  private turnFollowUp = false;
  /** Hard absolute backstop against runaway regardless of attribution: total automatic escalations across
   *  the whole conversation. Once it hits MAX_AUTO_ESCALATIONS, no integration turn offers escalate/re-delegate. */
  private autoEscalations = 0;
  private static readonly MAX_AUTO_ESCALATIONS = 8;
  /** Parked worker questions awaiting a (voice-relayed) user answer, keyed by ask id. */
  public readonly pendingAsks = new Map<string, { question: string; resolve: (answer: string) => void }>();

  /** SPECULATIVE REFLEX (VoiceEngine.onSpeculate → speculate()): the reflex turn starts on a stable
   *  PARTIAL transcript, its spoken output buffered — nothing reaches TTS until the endpointed final
   *  confirms via send() (which flushes the buffer and adopts the call as THE turn). A divergent final
   *  aborts it: output dropped, history rolled back, the final dispatches normally. */
  private spec?: {
    text: string;
    state: 'pending' | 'confirmed' | 'aborted';
    buf: Array<{ kind: string; message: string; data?: unknown }>; // held spoken-channel events
    ctl: AbortController;
    decide: (d: 'confirm' | 'abort') => void;   // resolved by send() (match/mismatch) or the safety timeout
    decision: Promise<'confirm' | 'abort'>;
    finalText?: string;                          // set on confirm — rewrites the transcript's user turn
    rec?: UserTurn;                              // set on confirm when the host named the turn (it can be replaced)
    done: Promise<RunResult>;                    // settles when the speculative turn fully finished/rolled back
  };
  /** Aborted speculative calls (double-billing visibility — each one paid for tokens never spoken). */
  public speculativeAbortedCalls = 0;
  /** Tools the reflex may execute on UNCONFIRMED (speculated) input — read-only lookups and the
   *  intentionally-silent Hold. Anything else (Act/Think/AnswerTask/CancelTask/ExitSession/memory
   *  writes/…) is a side effect on input the user may not have said: the guard ABORTS the speculation
   *  instead (the endpointed final then dispatches normally and may use the tool for real). */
  private static readonly SPEC_SAFE_TOOLS = new Set(['QuickLook', 'TaskStatus', 'Hold']);

  /** Lazily resolved memory tools (async loadMemory runs in initMemory). */
  private memoryReady: Promise<{ tools: AgentTool[]; index: string }> | undefined;

  constructor(options?: Partial<DuplexAgentOptions>) {
    this.options = { ...new DuplexAgentOptions(), ...options };
    const o = this.options;
    // Kick off async memory load early (resolves before first send via initMemory guard).
    if (o.memoryDir && o.fs) {
      this.memoryReady = loadMemory(o.fs, o.memoryDir, { maxWritesPerSession: 10, userDir: o.memoryUserDir });
    }
    // Resolve template slots in the voice prompt.
    const memSlot = o.memoryDir && o.fs
      ? VOICE_MEMORY_PROMPT
      : 'NEVER claim to have stored, saved, or remembered something durably — you cannot. Anything the user wants persisted (their name, preferences, notes) must go through Act so a worker writes it to memory.';
    const thinkSlot = o.thinkModel !== false ? THINK_GUIDANCE : THINK_DISABLED_GUIDANCE;
    // Tell the reflex up-front whether its worker can reach the web, so it dispatches web lookups to
    // Act instead of declining from priors (the weak reflex defaults to "I can't search" otherwise).
    const workerToolNames = (o.actOptions?.tools ?? []).map((t) => t.name);
    const canSearch = workerToolNames.some((n) => /WebSearch/i.test(n));
    const canFetch = workerToolNames.some((n) => /WebFetch/i.test(n));
    const workerWeb = canSearch
      ? ', and it CAN search the web and read web pages — so when the user gives you something specific to look up ("search for X", "find me…", "what\'s the latest on…"), route it to Act. But a bare capability QUESTION like "can you search the web?" just gets a short spoken "yes, I can" — do NOT dispatch and NEVER invent a query the user did not give you'
      : canFetch
        ? ', and it can fetch a specific web page URL (but cannot search the web)'
        : '';
    // MCP servers reach the worker via providerOptions (cursor/*) or as mcp__ tools — inject them so the
    // reflex KNOWS its real reach without a QuickLook (the weak reflex won't reliably look before it
    // answers "can you…?", and was FALSELY DENYING browser control it actually has).
    const mcpNames = [
      ...Object.keys((o.actOptions?.providerOptions as any)?.mcpServers ?? {}),
      ...new Set(workerToolNames.filter((n) => n.startsWith('mcp__')).map((n) => n.slice(5).split('__')[0])),
    ];
    const workerMcp = mcpNames.length
      ? `, and it can use these MCP servers: ${[...new Set(mcpNames)].join(', ')}` +
        (mcpNames.some((n) => /browser/i.test(n)) ? ' — including driving a REAL browser (open tabs, navigate, click, screenshot), so answer "yes" if asked whether you can control/drive a browser and route an actual browse to Act' : '')
      : '';
    const prompt = VOICE_SYSTEM_PROMPT
      .replace('{{MEMORY_SLOT}}', memSlot)
      .replace('{{THINK_SLOT}}', thinkSlot)
      .replace('{{WORKER_WEB}}', workerWeb + workerMcp)
      // Cross-session recall (host supplies QuickLook `sessions`): the reflex otherwise answers "we never did
      // that" from THIS transcript alone (live: 20260923-101910 denied a news run from the session before).
      + (o.quickLook?.sessions ? '\nPast conversations with this user are saved. When they refer to an EARLIER conversation or session ("last time", "the previous session", "you already did it before", "earlier today"), call QuickLook what:"sessions" (then what:"sessions" with path:"<id>" to read one) BEFORE answering — never say it does not exist without checking. Report only what the recap says: if it does not say why something happened, say you don\'t know — never guess a motive.' : '')
      + (o.voiceStyle === 'conversational' ? '\n' + VOICE_STYLE_CONVERSATIONAL : '')
      + (o.emotionTags ? '\n' + EMOTION_TAGS_GUIDANCE : '')
      + `\n${nowLine()}. Anchor every relative or time-sensitive reference — "today", "now", "current", "recent", "latest", "this year" — to THIS moment. When the user asks about something recent (news, sports, events), they mean near this date, not a well-known past instance; brief your worker with the current year so it searches for what is happening NOW, not a famous older event.`;
    const tools: AgentTool[] = [
      ...(o.reflexOptions?.tools ?? []),
      this.actTool(),
      ...(o.thinkModel !== false ? [this.thinkTool()] : []),
      this.taskStatusTool(), this.cancelTaskTool(), this.quickLookTool(), this.answerTaskTool(), this.holdTool(),
    ];
    // Tap the voice stream so we know whether the model actually SPOKE this turn (drives the
    // dead-air re-ack). Delegates the real host transparently — ask/confirm pass straight through.
    // (Explicit forwarding, not `{...o.host}`: spreading a class instance drops its prototype methods.)
    const host = o.host;
    const voiceHost: HostBridge | undefined = host && {
      ask: host.ask ? (q) => host.ask!(q) : undefined,
      confirm: host.confirm ? (p, m) => host.confirm!(p, m) : undefined,
      notify: (ev) => {
        // Sanitize the reflex's spoken stream: it must never emit a reserved [task …] event marker.
        // If it does, it's fabricating a result while the real task is still in flight — cut the
        // spoken text at the marker (keeping the legit ack) and suppress the rest of the turn so the
        // hallucinated report never reaches TTS. See RESERVED_EVENT_MARKER + mind/10.
        if (ev?.kind === 'text_delta' && typeof (ev as any).message === 'string') {
          if (this.heldThisTurn) return;   // Hold was called → stay silent; the filler is the only cue (the weak reflex often emits "…" anyway)
          if (this.fabricationCut) return; // already cut this turn → drop the tail
          // Double-ack guard: the reflex spoke BEFORE dispatching (that speech WAS the ack) — the
          // forced post-dispatch text step (toolChoice:'none') must not stack a second ack on top
          // (live: "Got it, let me grab…" then "Got it, gathering details now."). Drop its deltas.
          if (this.turnDispatched && this.spokeBeforeDispatch) return;
          this.reflexBuf += (ev as any).message as string;
          // Cut on either a full fabricated lifecycle marker OR a bare `[task` opener (garbled leak): the text
          // before it still goes through the gates, nothing after it (this turn) is ever spoken.
          const m = this.reflexBuf.match(RESERVED_EVENT_MARKER) ?? this.reflexBuf.match(RESERVED_EVENT_OPENER);
          if (m) {
            this.fabricationCut = true;
            log.warn(`reflex fabricated a [task …] event in its spoken stream — cutting it (kept ${m.index} chars)`);
            this.reflexBuf = this.reflexBuf.slice(0, Math.max(m.index!, this.reflexForwarded));
            this.pumpReflex(true);
            return;
          }
          this.pumpReflex(false);
          return;
        }
        // A step ended (next step starts / a tool runs): its text is complete — judge + release what the
        // sentence gate still holds, so an ack before a tool call isn't delayed behind the tool. A leak mutes the
        // rest of ITS step only — the next step (after a tool / the agent's own nudge) is a fresh reply.
        if (ev?.kind === 'turn_start' || ev?.kind === 'tool_use') {
          this.stepMarks.push(this.reflexBuf.length);
          if (this.leakStep) { this.leakStep = false; this.reflexForwarded = this.reflexBuf.length; }
          this.pumpReflex(false);
        }
        host.notify?.(ev);
      },
    };
    this.voice = new Agent({
      ai: o.ai,
      fs: new MemFilesystem(),
      model: o.reflexModel,
      stream: true,
      host: voiceHost,
      // The reflex IS the conversational channel — it confirms ambiguity inline ("did you mean…?"),
      // never via the blocking AskUserQuestion tool (Agent auto-adds it whenever a host is set). Left in,
      // it stalls a voice turn until the kill-switch. Worker questions still reach the user via parkQuestion.
      askUserQuestion: false,
      systemPrompt: prompt,
      instructionFiles: false,
      maxSteps: 8,
      timeoutMs: 30_000,
      failEmptyTurns: false, // a silent reflex turn after a dispatch is legitimate — the voice layer repairs it
      ...o.reflexOptions,
      tools,
      // Composed AFTER the spread so the dispatch guard can't be dropped by reflexOptions.
      hooks: composeHooks(this.dispatchGuard(), o.reflexOptions?.hooks),
    });
  }

  /** Resolve memory tools + inject index into voice system prompt (once). */
  private async initMemory(): Promise<void> {
    if (!this.memoryReady) return;
    const mem = await this.memoryReady;
    this.memoryReady = undefined; // one-shot
    // Append memory tools to the voice agent's active set.
    (this.voice.options.tools as AgentTool[]).push(...mem.tools);
    // Inject memory index into the system prompt.
    if (mem.index) this.voice.options.systemPrompt += '\n\n' + mem.index;
  }

  /** Forward what the reflex's spoken-stream gates allow (text_delta → host/TTS; forwarded audio can't be unsent):
   *  - hold a trailing fragment that could be a `[task` opener split across deltas ("[ta" + "sk"), and an UNCLOSED
   *    "(" (a stage direction is judged — and dropped — only once its ")" arrives; bounded at 80 chars);
   *  - the leak gate: whole sentences only, each screened by isLeakSuspect the moment it completes — a strict match
   *    is cut, any other suspect waits for the decider's verdict (judgeLeak) while the rest queues behind it.
   *  `final` = the stream is done: release the holds and judge whatever is left as a sentence. Text before a step
   *  boundary (stepMarks) is complete — no holds, its tail is a sentence. Idempotent; re-run as verdicts land. */
  private pumpReflex(final: boolean): void {
    for (;;) {
      if (this.leakStep) return;
      this.scrubStageDirections(); // drop complete "(waiting…)"-style asides before anything forwards
      const from = this.reflexForwarded;
      const buf = this.reflexBuf;
      const lastMark = Math.max(from, ...this.stepMarks);
      let upto = buf.length;
      if (!final && upto > lastMark) {
        const tail = buf.slice(Math.max(lastMark, upto - 6)).match(/\[\s*t?a?s?k?$/i);
        if (tail) upto -= tail[0].length;
        const paren = buf.lastIndexOf('(');
        if (paren >= lastMark && !buf.includes(')', paren) && buf.length - paren <= 80) upto = Math.min(upto, paren);
      }
      if (upto <= from) return;
      const region = buf.slice(from, upto);
      const ends = [...region.matchAll(SENTENCE_END)].map((m) => m.index! + m[0].length);
      for (const k of this.stepMarks) if (k > from && k <= upto) ends.push(k - from);
      if (final || (!ends.length && region.length >= 160)) ends.push(region.length); // a boundary-less run past 160 chars is judged as-is
      ends.sort((a, b) => a - b);
      let ok = 0, leak: string | undefined;
      for (const e of ends) {
        if (e <= ok) continue;
        const v = this.leakVerdict(region.slice(ok, e));
        if (v === undefined) break; // judge in flight — hold here, re-pumped when it lands
        if (v) { leak = region.slice(ok, e); break; }
        ok = e;
      }
      this.forwardReflex(from + ok);
      if (leak === undefined) return;
      // Cut: the clean sentences before it went out; the leak and the rest of ITS step are muted (a later step
      // speaks again), and the leak is scrubbed from the transcript at turn end.
      const at = from + ok;
      this.leakHit = true;
      this.leakCuts.push(leak.trim().slice(0, 40));
      log.warn(`reflex spoke its own analysis ("${leak.trim().slice(0, 40)}…") — cut before TTS`);
      this.notify('diag', 'analysis_leak', { text: leak.trim().slice(0, 80), at: this.turnSpoken.trim() ? 'mid_reply' : 'before_speech' });
      const next = this.stepMarks.find((k) => k > at);
      if (next === undefined) { this.reflexForwarded = buf.length; this.leakStep = true; return; }
      this.reflexForwarded = next; // that step already ended — carry on with the next one
    }
  }

  private forwardReflex(to: number): void {
    const out = this.reflexBuf.slice(this.reflexForwarded, to);
    this.reflexForwarded = Math.max(this.reflexForwarded, to);
    if (!out) return;
    if (out.trim()) this.spokeThisTurn = true;
    this.turnSpoken += out;
    this.emitHost({ kind: 'text_delta', message: out });
  }

  /** Leak verdict for one complete sentence: false = speak, true = cut, undefined = the judge is still out.
   *  Non-suspects pass free; strict shapes are cut without a call; other suspects go to the decider (if any) —
   *  one call at a time, cached per turn. A failed/slow judge fails OPEN (speech is never muted on a guess). */
  private leakVerdict(sentence: string): boolean | undefined {
    const s = sentence.trim();
    if (!s || !isLeakSuspect(s)) return false;
    if (isStrictLeak(s)) return true;
    const decider = this.options.decider;
    if (!decider) return false;
    const known = this.leakVerdicts.get(s);
    if (known !== undefined || this.judging) return known;
    const gen = this.turnGen, t0 = performance.now();
    const userSaid = [...this.voice.transcript].reverse()
      .map((m) => (m.role === 'user' ? contentText(m.content) : ''))
      .find((t) => t && !t.startsWith('[reminder]')) ?? '';
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error(`no verdict in ${DuplexAgent.LEAK_JUDGE_TIMEOUT_MS}ms`)), DuplexAgent.LEAK_JUDGE_TIMEOUT_MS); });
    this.judging = Promise.race([judgeLeak(decider, s, userSaid), timeout])
      .then((p) => {
        this.notify('diag', 'leak_judge', { text: s.slice(0, 80), p: Math.round(p * 100) / 100, ms: Math.round(performance.now() - t0) });
        return p >= LEAK_JUDGE_THRESHOLD;
      }, (e) => { log.warn(`leak judge failed — speaking the sentence: ${(e as Error).message}`); return false; })
      .then((leak) => {
        clearTimeout(timer);
        if (gen !== this.turnGen) return; // the stream it was judging is gone (new turn / repair / replaced)
        this.judging = undefined;
        this.leakVerdicts.set(s, leak);
        this.pumpReflex(false);
      });
    return undefined;
  }
  private static readonly LEAK_JUDGE_TIMEOUT_MS = 1500;

  /** The stream is done: settle every pending verdict and release everything the gates still hold. */
  private async drainReflex(): Promise<void> {
    for (;;) {
      this.pumpReflex(true);
      if (!this.judging) return;
      await this.judging;
    }
  }

  /** Drop any in-flight judge of the current stream (its verdict must not pump a stream that moved on). */
  private dropJudge(): void { this.turnGen++; this.judging = undefined; }

  /** Remove a cut analysis leak from this turn's assistant message(s) — left in, it primes the next turn to
   *  leak again and reads as something we said. Keeps any legit prefix; drops a message left empty. */
  private scrubLeakFromTranscript(): void {
    const keys = this.leakCuts.splice(0);
    const t = this.voice.transcript;
    for (const key of keys) {
      let i = t.length - 1;
      while (i >= this.turnBase && !(t[i].role === 'assistant' && typeof t[i].content === 'string' && (t[i].content as string).includes(key))) i--;
      if (i < this.turnBase) { log.warn('analysis leak cut, but its text was not found in the transcript to scrub'); continue; }
      const m = t[i] as { content: string; tool_calls?: unknown[] };
      m.content = m.content.slice(0, m.content.indexOf(key)).trimEnd();
      if (!m.content && !m.tool_calls?.length) t.splice(i, 1);
    }
  }

  /** A Hold turn is silent BY DESIGN: every reflex text after the Hold is muted (voiceHost drops it), so none of it may
   *  persist either — the transcript is what the agent SAID. Live 20260923-125143 msg 10: the model's reasoning ("We
   *  need to respond according to guidelines… So we should hold") was saved as an assistant reply and fed every later
   *  turn. Strips the text of assistant messages after the Hold call (tool calls stay — they happened); a message left
   *  empty is removed. */
  private scrubHeldText(): void {
    if (!this.heldThisTurn) return;
    const t = this.voice.transcript;
    let i = this.turnBase;
    while (i < t.length && !t[i].tool_calls?.some((c) => c.function.name === 'Hold')) i++;
    for (let j = t.length - 1; j > i; j--) {
      const m = t[j];
      if (m.role !== 'assistant' || !contentText(m.content).trim()) continue;
      if (m.tool_calls?.length) m.content = '';
      else t.splice(j, 1);
    }
  }

  /** Shared turn epilogue (user turn, confirmed speculation, re-voice): release held text, scrub a cut leak,
   *  then repair dead air — unless the turn was ABORTED: the user took the floor (barge-in / Esc), which is
   *  not dead air, and a nudge would run on the same aborted signal (live: the "[reminder]" landed in the
   *  transcript as a user turn with no reply, session 20260923-101910 msg 27). */
  private async endTurn(res: RunResult | undefined, fallback?: string): Promise<void> {
    if (res?.finishReason === 'aborted') { this.dropJudge(); this.scrubLeakFromTranscript(); this.scrubHeldText(); return; } // the user has the floor — speak nothing more
    await this.drainReflex();
    this.scrubLeakFromTranscript();
    this.scrubHeldText();
    // Only a turn that voiced NOTHING is repaired. A leak after clean speech just mutes the remainder: the listener
    // already heard a reply, and a "finish your reply" call mostly re-answered a reply that was already complete.
    if (this.silentTurn) await this.ackIfSilent(fallback);
  }

  /** Remove complete stage-direction parentheticals from the UNFORWARDED reflex text (STAGE_DIRECTION_RE).
   *  Only the unforwarded region is touched — already-spoken audio can't be unsent, and splicing before
   *  reflexForwarded would corrupt the forward offset. */
  private scrubStageDirections(): void {
    if (this.reflexForwarded >= this.reflexBuf.length) return;
    const region = this.reflexBuf.slice(this.reflexForwarded);
    const scrubbed = region.replace(/\([^()]*\)/g, (s) => (STAGE_DIRECTION_RE.test(s) ? '' : s));
    if (scrubbed !== region) this.reflexBuf = this.reflexBuf.slice(0, this.reflexForwarded) + scrubbed;
  }

  /** Clear the per-turn guards. Called at the head of every voice turn (user send + re-voice flush). */
  private resetTurn(): void {
    this.turnDispatched = false;
    this.spokeBeforeDispatch = false;
    this.turnBriefs.clear();
    this.spokeThisTurn = false;
    this.externalSpeech = false;
    this.heldThisTurn = false;
    this.reflexBuf = '';
    this.reflexForwarded = 0;
    this.fabricationCut = false;
    this.leakCuts = [];
    this.leakStep = this.leakHit = false;
    this.stepMarks = []; this.leakVerdicts.clear(); this.turnSpoken = ''; this.offerOpen = false;
    this.dropJudge();
    this.turnBase = this.voice.transcript.length;
    this.turnFollowUp = false; // user turns + clean re-voices never spawn auto-follow-ups
    this.voice.options.toolChoice = undefined; // clear any post-dispatch tool lock from the previous turn
  }

  /** preToolUse guard on the reflex: once it has dispatched this turn, a dispatch is "said my piece,
   *  now wait for the push" (CC's Task model). Block the temptations — TaskStatus polling and identical
   *  re-dispatch — so the only remaining move is to voice a short ack and end. A genuinely NEW Act is
   *  still allowed (parallel independent work). During a re-ack pass, block every tool. */
  private dispatchGuard(): Hooks {
    return {
      preToolUse: (call: ToolUse) => {
        // Speculation tool-safety: side-effectful tools must NEVER run on unconfirmed (speculated)
        // input — dispatching Act on words the user may not have said is dangerous. Abort the whole
        // speculative call instead (its cost is the speculative_aborted_calls metric); the endpointed
        // final dispatches normally and may then use the tool for real. Read-only lookups + Hold are
        // allowed (their results are as valid for the confirmed final as for the partial).
        if (this.spec?.state === 'pending' && !DuplexAgent.SPEC_SAFE_TOOLS.has(call.name)) {
          log.verbose(`speculation aborted: reflex called ${call.name} on unconfirmed input`);
          this.abortSpeculation();
          return { block: true, reason: 'Speculative turn aborted.' };
        }
        if (this.nudging) return { block: true, reason: 'Just say one short spoken acknowledgement — no tools this turn.' };
        if (!this.turnDispatched) return;
        if (call.name === 'TaskStatus')
          return { block: true, reason: 'You just dispatched a task this turn — do NOT poll. Give one short spoken acknowledgement and end your turn; the result arrives later as a [task …] event.' };
        if ((call.name === 'Act' || call.name === 'Think') && this.turnBriefs.has(String(call.args?.brief ?? '')))
          return { block: true, reason: 'You already dispatched this exact task — acknowledge briefly and end your turn.' };
      },
    };
  }

  /** The host spoke on this turn's behalf OUTSIDE the reflex stream (e.g. the voice engine's adaptive
   *  micro-ack). For a DISPATCHED turn that's a sufficient ack (don't stack a second one); for an
   *  inline turn whose reply came back empty it is NOT — "One sec." followed by permanent silence is
   *  still dead air, so silentTurn ignores external speech unless work was dispatched. */
  noteExternalSpeech(): void { this.externalSpeech = true; }

  /** True when the just-finished turn voiced NOTHING — dead air to repair. Two ways this happens:
   *  (a) a dispatch with no spoken ack, and (b) an INLINE turn whose `final` channel came back empty —
   *  gpt-oss harmony sometimes puts the whole reply in `analysis` (→ thinking_delta, suppressed in
   *  voice) and emits an empty `final`, so no text_delta ever streams. Both ship silence; both repair.
   *  Requires a host: without one there's no stream to detect speech on (and no one to speak to). */
  private get silentTurn(): boolean {
    const ackedByHost = this.externalSpeech && this.turnDispatched; // external ack only satisfies a dispatched turn
    return !!this.options.host && !this.spokeThisTurn && !ackedByHost && !this.heldThisTurn; // a Hold turn is intentionally silent — not dead air
  }

  /** A turn that voiced nothing is dead air. Re-prompt the reflex ONCE so the LLM itself voices a short
   *  line (no template). If it STILL says nothing, fall back to a minimal line so silence never ships.
   *  Wording adapts to whether work was dispatched (an ack) or the inline reply was simply lost. */
  private async ackIfSilent(fallback?: string): Promise<void> {
    const dispatched = this.turnDispatched;
    const reminder = fallback
      ? '[reminder] You said nothing to the user this turn. Tell them, in ONE short spoken sentence, what just happened — no tools.'
      : dispatched
      ? '[reminder] You dispatched a task but said nothing to the user. Say ONE short spoken acknowledgement now — no tools.'
      : '[reminder] You said nothing to the user this turn. Give your ONE short spoken reply now — no tools.';
    const base = this.voice.transcript.length;
    // The nudge is a fresh spoken stream: a cut (fabricated marker / analysis leak) in the turn being repaired
    // must not also mute its repair — that shipped the canned fallback instead of the model's reply.
    this.fabricationCut = this.leakStep = false; this.reflexBuf = ''; this.reflexForwarded = 0; this.stepMarks = []; this.dropJudge();
    this.nudging = true;
    try {
      await this.voice.send(reminder);
    } catch (e) {
      log.warn(`ack nudge failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      this.nudging = false;
      // The reminder is an internal repair prompt, never a user turn: keep the reply it produced, drop the
      // prompt (left in, it reads as the user speaking and a later turn "answers" it).
      const i = this.voice.transcript.findIndex((m, k) => k >= base && m.role === 'user' && m.content === reminder);
      if (i >= 0) this.voice.transcript.splice(i, 1);
      await this.drainReflex();
      this.scrubLeakFromTranscript();
    }
    // If the reflex STILL voiced nothing, ship a fallback line so silence never reaches the user.
    // A caller-supplied fallback (e.g. a task failure) takes priority — that information must not be
    // swallowed. Small varied pools with no immediate repeat (a fixed line reads robotic when it recurs).
    if (!this.spokeThisTurn) {
      // Never ask the user to repeat themselves when the silence is OUR filter's doing (the reply, and its
      // repair, were cut as analysis leaks) — the user said nothing wrong.
      const pool = fallback ? [fallback] : dispatched ? DuplexAgent.FALLBACK_ACKS : this.leakHit ? DuplexAgent.FALLBACK_OWN : DuplexAgent.FALLBACK_RETRY;
      const fresh = pool.filter((p) => p !== this.lastFallback);
      const line = (fresh.length ? fresh : pool)[Math.floor(Math.random() * (fresh.length || pool.length))];
      this.lastFallback = line;
      this.emitHost({ kind: 'text_delta', message: line });
    }
  }

  /** Dead-air fallback pools (see ackIfSilent). Both retry lines keep the 'say that again' phrase —
   *  hosts/tests key on it. */
  private static readonly FALLBACK_ACKS = ['Okay, on it.', 'On it.', 'Alright, working on it.'];
  private static readonly FALLBACK_RETRY = ['Sorry, could you say that again?', 'Hm, I missed that — could you say that again?'];
  private static readonly FALLBACK_OWN = ['Sorry, I lost my train of thought there — give me that one more time?', 'Hm, I got tangled up — ask me that once more?'];
  private lastFallback = '';

  /** One user turn: the voice agent streams the reply (and may Act/Think). Serialized with re-voice turns.
   *  If a speculative reflex call is pending and this content CONFIRMS it (the final matches the
   *  speculated partial), the in-flight call is adopted as THE turn: its buffered output flushes to the
   *  host NOW (this is the latency win) and streaming continues live. Any other content aborts the
   *  speculation first (rolled back silently) and runs a normal turn behind it.
   *  `opts.id` names the turn; `opts.replaces` names an earlier one this content REPLACES — the voice engine's
   *  fuller re-finalization of an utterance whose reply had not started ("…previous" → "…previous
   *  conversation."). The replaced turn is skipped if it has not run yet, else aborted and rolled back out of
   *  the transcript (kept only if it already dispatched work) — one reply, to the full text. */
  send(content: MessageContent, opts?: { id?: string; replaces?: string }): Promise<RunResult> {
    if (opts?.replaces) this.replaceTurn(opts.replaces);
    // The host's per-turn signal (the CLI's Esc/barge controller), captured before any await — not the combined
    // signal a running named turn installed (aborting THAT turn must not abort this one).
    const cur = this.voice.options.signal;
    const ext = cur && cur === this.turnSignal?.own ? this.turnSignal.ext : cur;
    const rec = opts?.id ? this.trackTurn(opts.id) : undefined;
    if (rec?.superseded) { this.notify('diag', 'turn_replaced', { id: opts!.id, ran: false }); return Promise.resolve(DuplexAgent.replacedResult(this.voice.transcript)); }
    const reflexCut = this.reflexCut; this.reflexCut = false; // one line resolves one barge
    // Auto-resume after a trivial barge: the user cut off a task delivery, then said something with NO
    // new request ("sorry", "go on") — resume the parked result instead of running an empty turn. Runs
    // before speculation so a filler partial that speculated is rolled back, not adopted.
    if (typeof content === 'string' && this.hasParkedDelivery()) {
      const fallback = () => (isTrivialBarge(content) ? 'resume' as const : 'steer' as const);
      // The last thing voiced was the cap offer and all that is parked is that task's rest: a plain yes / "go on" / "tell
      // me more" accepts it — no judge call (its ~0.3-1s would delay the answer). Any other spoken question (another
      // task's, a rhetorical one, the worker's own) is no offer to resume: the judge / the reflex answers it.
      const accepted = this.offerOpen && this.parkedRedeliver.length > 0 && this.parkedRedeliver.every((p) => p.task === this.offer.task)
        && (isOfferAcceptance(content) || isTrivialBarge(content));
      const judge = accepted ? undefined : this.options.bargeIntent;
      return (accepted ? Promise.resolve('resume' as const) : judge ? judge(content).catch((e) => { log.warn(`bargeIntent judge failed — using the allowlist: ${(e as Error).message}`); return fallback(); }) : Promise.resolve(fallback()))
        .then((intent) => {
          this.notify('diag', 'barge_intent', { intent, text: content.slice(0, 80) });
          if (intent === 'resume' && !reflexCut) return this.resumeParked();
          // Aside — the reflex answers it and carries the delivery on ITSELF, in the same turn, told what was heard and
          // what was not (the reply-interruption note): replaying the parked words verbatim after the answer re-voiced
          // what the answer had just said (live 20260923-192543: the use-case sentence spoken ~5 times). Only what it
          // was told is consumed; anything a still-running task parks meanwhile follows verbatim, then the task goes
          // live. A "go on" over a cut REFLEX reply is the reflex's to continue: run it, then resume the parked delivery.
          // Not when that turn was aborted: the user barged again (or a re-final replaced it), and the next line
          // decides the parked delivery (a resume here would talk over them and revive what they cut).
          if (intent === 'aside' || intent === 'resume') {
            const told = intent === 'aside' ? [...this.parkedRedeliver] : [];
            return this.sendTurn(intent === 'aside' ? content + this.asideNote(told) : content, true, rec, ext)
              .then(async (r) => {
                // Read synchronously: the turn's per-turn state is still this turn's (the next queued turn's resetTurn
                // runs after this reaction). Held (an unfinished fragment) → nothing was delivered: keep it all parked
                // for the next line and speak nothing over the user (live: Hold on "But isn't it" lost the pricing).
                if (r.finishReason === 'aborted' || this.heldThisTurn) return r;
                // Consume only what the answer actually carried on: a clause it dropped stays parked (not replayed —
                // a later "go on" or aside surfaces it). Live: 1 of 9 asides dropped the "waitlist" clause. Judged per
                // clause, paraphrase by the decider (voice/unsaid.ts) — detached: the turn ends now (the host closes its
                // speech on it); the verdict lands ~0.3-3s later. A later user turn (a steer queued during this one)
                // moved on meanwhile → nothing to re-park.
                const missed = unsaidClauses(told.map((p) => p.text), this.turnSpoken, this.options.decider);
                const turn = this.turn;
                this.parkedRedeliver = this.parkedRedeliver.filter((p) => !told.includes(p));
                await this.resumeParked();
                void missed.then((m) => {
                  // An offer question is no unsaid content: re-parked alone it replays as a stale "Want me to…?".
                  const dropped = m.filter((c) => !isQuestion(c)).join(' ').replace(/[,;]$/, '.');
                  if (!dropped || this.turn !== turn) return;
                  this.parkedRedeliver.unshift({ text: dropped, task: told.at(-1)?.task });
                  this.notify('diag', 'aside_dropped', { text: dropped.slice(0, 80) });
                });
                return r;
              });
          }
          return this.sendTurn(content, false, rec, ext);
        });
    }
    return this.sendTurn(content, false, rec, ext);
  }

  /** Host-named user turns (send opts.id), so a later send can replace one. Bounded; a replace that arrives
   *  BEFORE its target (the host's dispatch hops can reorder) leaves a pre-superseded record behind. */
  private userTurns = new Map<string, UserTurn>();
  private turnSignal?: { own: AbortSignal; ext?: AbortSignal }; // the combined signal a named turn installed while it runs
  private trackTurn(id: string): UserTurn {
    let rec = this.userTurns.get(id);
    if (!rec) {
      rec = { ctl: new AbortController(), done: false, superseded: false };
      this.userTurns.set(id, rec);
      if (this.userTurns.size > 32) this.userTurns.delete(this.userTurns.keys().next().value!);
    }
    return rec;
  }
  private replaceTurn(id: string): void {
    const rec = this.trackTurn(id);
    // Already over. Aborted before it dispatched or said anything (the user talking on over its ack) → a fragment of
    // this utterance: erase it, so the transcript keeps the user's final words once (live 20260923-192543: four
    // growing fragments, msgs 31–34). Queued, so it lands between turns, before the replacing turn runs. Answered
    // (e.g. a Hold) or real work → it stays; the fuller text is simply the next turn.
    if (rec.done) {
      const drop = rec.dropped; rec.dropped = undefined;
      if (drop?.length) void this.enqueue(async () => {
        const t = this.voice.transcript;
        for (const m of drop) { const i = t.indexOf(m); if (i >= 0) t.splice(i, 1); }
        this.notify('diag', 'turn_replaced', { ran: true, rolledBack: true, late: true });
      });
      return;
    }
    if (rec.superseded) return;
    rec.superseded = true;
    rec.ctl.abort();
  }
  private static replacedResult(messages: RunResult['messages']): RunResult {
    return { text: '', steps: 0, finishReason: 'aborted', messages } as RunResult;
  }
  /** A turn replaced mid-run: mute whatever it still holds, and erase it from the transcript (the replacing turn
   *  carries the user's full words) — unless it already dispatched work or SPOKE: the task is real, and the user
   *  heard those words, so both stay visible to the replacing turn. An aborted stream never reaches the transcript,
   *  so what was spoken is recorded as the replaced turn's reply. */
  private dropReplacedTurn(): RunResult {
    this.fabricationCut = true;
    this.reflexBuf = this.reflexBuf.slice(0, this.reflexForwarded);
    this.dropJudge();
    const spoke = this.turnSpoken.trim();
    const rolledBack = !this.turnDispatched && !spoke;
    if (rolledBack) this.voice.transcript.length = this.turnBase;
    else if (spoke) {
      const t = this.voice.transcript;
      const have = t.slice(this.turnBase).filter((m) => m.role === 'assistant').map((m) => contentText(m.content)).join(' ');
      if (!have.includes(spoke)) t.push({ role: 'assistant', content: spoke });
    }
    this.notify('diag', 'turn_replaced', { ran: true, rolledBack, spoke: !!spoke });
    return DuplexAgent.replacedResult(this.voice.transcript);
  }

  private resumeParked(): Promise<RunResult> {
    if (this.spec) this.abortSpeculation();
    return this.enqueue(async () => {
      this.resetTurn();
      this.redeliverParked(); // everything unheard so far, now, in order
      // …and a barge-parked task still running delivers the rest LIVE, behind it (live: a "go on" before the task
      // settled left its final result parked unspoken, and a later steer dropped it). One the user moved on from stays.
      for (const t of this.tasks.values()) if (t.status === 'running' && t.deliveryParked === 'barge') t.deliveryParked = undefined;
      return { text: '', steps: 0, finishReason: 'stop', messages: [] } as RunResult;
    });
  }

  private sendTurn(content: MessageContent, keepParked = false, rec?: UserTurn, ext?: AbortSignal): Promise<RunResult> {
    const spec = this.spec;
    if (spec?.state === 'pending') {
      if (typeof content === 'string' && speculationConfirms(spec.text, content)) {
        spec.state = 'confirmed';
        spec.finalText = content;
        if (rec) { spec.rec = rec; rec.ctl.signal.addEventListener('abort', () => spec.ctl.abort(), { once: true }); }
        for (const ev of spec.buf.splice(0)) this.options.host?.notify?.(ev); // flush the held output NOW
        spec.decide('confirm');
        this.notify('diag', 'speculation_confirmed', { text: spec.text.slice(0, 80) }); // host diag sinks record it; UIs ignore 'diag'
        return spec.done;
      }
      this.abortSpeculation(); // divergent final (or non-text turn) — the normal turn queues behind the rollback
    }
    return this.enqueue(async () => {
      await this.initMemory();
      this.resetTurn();
      this.turn++;
      // Drop any pending auto-resume: the user moved on from every barge-parked task (else a much-later "go on"
      // replays a stale result, or a later barge revives it). Its result still lands in the transcript (folded at
      // settle), so it stays recoverable by asking.
      if (!keepParked) {
        this.parkedRedeliver = [];
        for (const t of this.tasks.values()) if (t.deliveryParked === 'barge') t.deliveryParked = 'moved_on';
      }
      if (rec?.superseded) { this.notify('diag', 'turn_replaced', { ran: false }); return DuplexAgent.replacedResult(this.voice.transcript); } // replaced while queued — never runs
      const signal = rec && (ext ? AbortSignal.any([ext, rec.ctl.signal]) : rec.ctl.signal);
      if (signal) { this.voice.options.signal = signal; this.turnSignal = { own: signal, ext }; }
      let res: RunResult;
      try { res = await this.voice.send(content); }
      finally { if (signal) { this.voice.options.signal = ext; this.turnSignal = undefined; } if (rec) rec.done = true; }
      if (rec?.superseded) return this.dropReplacedTurn();
      if (rec && res.finishReason === 'aborted' && !this.turnDispatched && !this.turnSpoken.trim()) rec.dropped = this.voice.transcript.slice(this.turnBase);
      await this.endTurn(res);
      return res;
    });
  }

  /** Start a HELD speculative reflex turn on a stable partial (VoiceEngine.onSpeculate). Its spoken
   *  output buffers in emitHost; send() later confirms (flush + adopt) or aborts it. The turn holds the
   *  voice mutex until that decision (bounded by a safety timeout), so no other turn can interleave
   *  into the transcript between the speculative messages and their rollback. No-op if a speculation
   *  is already in flight. */
  speculate(text: string): void {
    if (!text.trim() || this.spec) return;
    let decide!: (d: 'confirm' | 'abort') => void;
    const decision = new Promise<'confirm' | 'abort'>((r) => { decide = r; });
    const spec: NonNullable<DuplexAgent['spec']> = {
      text, state: 'pending', buf: [], ctl: new AbortController(), decide, decision, done: undefined as unknown as Promise<RunResult>,
    };
    this.spec = spec;
    spec.done = this.enqueue(async () => {
      const empty: RunResult = { text: '', steps: 0, finishReason: 'aborted', messages: this.voice.transcript };
      if (spec.state === 'aborted') { this.speculativeAbortedCalls++; if (this.spec === spec) this.spec = undefined; return empty; } // aborted before it ever started — nothing to roll back
      await this.initMemory();
      this.resetTurn();
      const base = this.voice.transcript.length;
      const prevSignal = this.voice.options.signal;
      this.voice.options.signal = spec.ctl.signal;
      let res: RunResult | undefined;
      try { res = await this.voice.send(spec.text); }
      catch (e) { log.warn(`speculative turn failed: ${e instanceof Error ? e.message : e}`); }
      finally { this.voice.options.signal = prevSignal; }
      // Stream done — await the endpoint decision. Bounded: a final that never dispatches (dropped by
      // an engine guard, mode change, …) must not wedge the voice queue behind a zombie speculation.
      const timer = setTimeout(() => { spec.state = spec.state === 'pending' ? 'aborted' : spec.state; spec.decide('abort'); }, 10_000);
      (timer as any).unref?.();
      const d = await spec.decision;
      clearTimeout(timer);
      if (d === 'abort') {
        // The speculation never happened: drop its output (emitHost already ate it), erase its messages
        // (the transcript must contain the FINAL text only — the real turn dispatches right behind us),
        // and count the spend.
        if (this.voice.transcript.length > base) this.voice.transcript.length = base;
        this.speculativeAbortedCalls++;
        log.verbose(`speculation aborted (${this.speculativeAbortedCalls} total): "${spec.text.slice(0, 50)}"`);
        if (this.spec === spec) this.spec = undefined;
        return res ?? empty;
      }
      // CONFIRMED — the transcript must carry what the user actually SAID (the final), not the partial.
      for (let i = base; i < this.voice.transcript.length; i++) {
        const m = this.voice.transcript[i];
        if (m.role === 'user' && contentText(m.content) === spec.text) { m.content = spec.finalText!; break; }
      }
      this.spec = undefined; // live from here (late emitHost stragglers pass straight through)
      if (spec.rec) spec.rec.done = true;
      if (spec.rec?.superseded) return this.dropReplacedTurn();
      await this.endTurn(res);
      return res ?? empty;
    });
  }

  /** Abort a pending speculation (idempotent): kill the in-flight call, drop its buffered output.
   *  Called on divergence (send), a stale partial (VoiceEngine.onSpeculateAbort), a side-effect tool
   *  attempt (dispatchGuard), or host paths that will never dispatch a final (commands, voice-off). */
  abortSpeculation(): void {
    const spec = this.spec;
    if (spec?.state !== 'pending') return;
    spec.state = 'aborted';
    spec.buf.length = 0;
    spec.ctl.abort();
    spec.decide('abort');
    this.notify('diag', 'speculation_aborted', { text: spec.text.slice(0, 80) });
  }

  /** Cancel a running background task — shared by the CancelTask tool and the CLI /tasks picker. */
  cancelTask(id: string): string {
    const rec = this.tasks.get(id);
    if (!rec) return `No task '${id}'.`;
    if (rec.status !== 'running') return `Task ${rec.id} is already ${rec.status}.`;
    rec.status = 'cancelled';
    rec.controller.abort();
    return `Task ${rec.id} (${rec.label}) cancelled.`;
  }

  /** Barge-in (or a dispatch-driven supersede): suppress running tasks' remaining SPOKEN delivery so a
   *  superseded topic never talks over the new one (the debt-after-jokes regression). Tasks keep running and
   *  still fold their result into the transcript — recoverable, just not spoken. Returns parked ids (logging).
   *  Does NOT cancel. Two callers, two shapes:
   *   - BARGE (default): the user interrupted AUDIO. Park EVERY live running task (including this turn's) — the
   *     worker they cut off must stop, whatever turn launched it — as 'barge', so a following "go on" can bring
   *     it back. A task the user already moved on from stays moved on: a barge never revives it.
   *   - SUPERSEDE (`priorTurnsOnly`, `barge:false`, from dispatch()): a new dispatch redirected to fresh work.
   *     Park only PRIOR-turn live tasks so parallel siblings dispatched in the SAME turn don't silence each other,
   *     as 'moved_on' (don't resurface on a stray "go on"). */
  parkInFlightDeliveries(barge = true, priorTurnsOnly = false): string[] {
    if (barge) { this.bargeMark = this.parkedRedeliver.length; this.reflexCut = false; this.offerOpen = false; } // cut off: the offer was not what they last heard (parkCutDelivery re-opens it when the cut WAS the offer)
    const parked: string[] = [];
    for (const rec of this.tasks.values())
      if (rec.status === 'running' && !rec.deliveryParked && (!priorTurnsOnly || rec.createdTurn < this.turn)) { rec.deliveryParked = barge ? 'barge' : 'moved_on'; parked.push(rec.id); }
    return parked;
  }

  /** A barge-in cut a worker delivery the VOICE ENGINE was still speaking — the queued utterances it dropped plus the
   *  unheard rest of the one playing (cli splitCut). It joins the parked delivery AHEAD of whatever parked tasks
   *  produced since the barge (it was voiced first); the user's next line runs the barge-intent judge — resume it,
   *  answer an aside and carry it on, or drop it on a steer/stop. `heard` = the cut utterance's part the user DID hear
   *  (the aside note tells the reflex what was heard vs missed).
   *  Live 20260923-125143: "Oh, that— that's new." cut an iOS-news delivery and its 6 remaining utterances vanished.
   *  `reflexCut` = the barge cut a REFLEX reply (the host's cut had a reply): a "go on" then runs the reflex (see send).
   *  A cut spoken by a task the user moved on from (or whose outcome went to an integration turn) is not parked. */
  parkCutDelivery(text: string, reflexCut = false, heard = ''): void {
    this.reflexCut = reflexCut;
    const t = text.trim();
    if (!t) return;
    // The cut was the cap offer itself (the user was hearing it, so a plain yes accepts it — send), or the worker's own
    // closing question: never replayed or parked alone.
    if (this.offer.text && this.offer.text.endsWith(t)) { this.offerOpen = !reflexCut; return; }
    if (t.split(SENTENCE_SPLIT).every(isQuestion)) return;
    const first = t.split(/(?<=[.!?])\s/)[0]; // the playing utterance's unheard rest (starts at its cut word or sentence)
    const task = [...this.voicedBy].reverse().find(([seg]) => t.includes(seg) || (first.length >= 16 && seg.includes(first)))?.[1];
    if (task && this.tasks.get(task)?.deliveryParked === 'moved_on') return;
    this.parkedRedeliver.splice(Math.min(this.bargeMark, this.parkedRedeliver.length), 0, { text: t, task, ...(heard ? { heard } : {}) });
    this.notify('diag', 'delivery_parked', { chars: t.length });
  }

  /** One spoken segment of a task's delivery — the ONLY path a worker's speech takes (streamed segments, the settle
   *  tail, the no-spoken fallback). Live → the voice queue; barge-parked → the parked delivery, in order; superseded
   *  (the user moved on) → nowhere (the result still folds into the transcript at settle). */
  private deliverSegment(rec: TaskRecord | undefined, seg: string): void {
    if (!seg.trim()) return;
    if (rec) rec.spokenText = (rec.spokenText ? rec.spokenText + ' ' : '') + seg;
    if (!rec?.deliveryParked) {
      const [say, rest] = rec ? this.budgetSplit(rec, seg) : [seg, ''];
      if (say) {
        this.offerOpen = false; // spoken after the cap offer: it is no longer what the user last heard
        this.options.host?.notify?.({ kind: 'speak_utterance', message: say });
        if (rec) { this.voicedBy.set(say.trim(), rec.id); if (this.voicedBy.size > 64) this.voicedBy.delete(this.voicedBy.keys().next().value!); }
      }
      if (!rest || !rec) return;
      // Over budget: park the rest exactly like a barge-cut delivery (a resume revives it — and the still-running task —
      // once; a steer drops it), and offer it instead of monologuing on.
      rec.deliveryParked = 'barge';
      this.parkedRedeliver.push({ text: rest, task: rec.id });
      if (this.options.spokenOffer) {
        this.offer = { text: rec.capOffer = this.options.spokenOffer, task: rec.id };
        this.offerOpen = true;
        this.options.host?.notify?.({ kind: 'speak_utterance', message: this.offer.text });
      }
      this.notify('diag', 'delivery_budget', { task: rec.id, spoke: rec.voicedChars, parked: rest.length });
    } else if (rec.deliveryParked === 'barge') this.parkedRedeliver.push({ text: seg, task: rec.id });
  }

  /** Split a delivery segment at the spoken budget (whole sentences; the first sentence always speaks): what to voice
   *  now, and the rest to park. Marks the budget spent on a cut, so a resumed delivery runs on unbudgeted. */
  private budgetSplit(rec: TaskRecord, seg: string): [string, string] {
    const cap = this.options.spokenBudgetChars;
    if (!cap || rec.budgetSpent) return [seg, ''];
    const sents = seg.trim().split(SENTENCE_SPLIT);
    let used = rec.voicedChars ?? 0, n = 0;
    while (n < sents.length && (!used || used + sents[n]!.length <= cap)) used += sents[n++]!.length + 1;
    const over = sents.slice(n);
    // All that overflows is ONE short closing question (the worker's offer): speak it — parked alone it would replay
    // stale on "yes", and the cap would add a second offer after it. Once per task (segments stream in one by one);
    // more, or longer, is cut like any text.
    if (!over.length || (over.length === 1 && isQuestion(over[0]!) && over[0]!.length <= OFFER_OVERRUN && used <= cap)) {
      rec.voicedChars = used + (over[0] ? over[0].length + 1 : 0);
      return [seg, ''];
    }
    rec.voicedChars = used;
    rec.budgetSpent = true;
    return [sents.slice(0, n).join(' '), sents.slice(n).join(' ')];
  }
  /** The cap offer last spoken and the task whose rest it offers — a barge over it is not a delivery to park
   *  (parkCutDelivery). */
  private offer = { text: '', task: '' };
  /** The cap offer was the last thing voiced (no delivery or turn has spoken since) — a plain yes accepts it (send). */
  private offerOpen = false;

  /** The task's outcome now belongs to its integration turn (failed / stopped early): take back its parked pieces and
   *  keep it from being revived — a "go on" must not replay raw segments of a result the reflex is about to explain. */
  private dropParked(rec: TaskRecord): void {
    this.parkedRedeliver = this.parkedRedeliver.filter((p) => p.task !== rec.id);
    if (rec.deliveryParked) rec.deliveryParked = 'moved_on';
  }

  /** True while there is a parked delivery the next line may resume: unheard text already parked, or a barge-parked
   *  task still running (one the user moved on from does not count). */
  private hasParkedDelivery(): boolean {
    return this.parkedRedeliver.length > 0 || [...this.tasks.values()].some((t) => t.status === 'running' && t.deliveryParked === 'barge');
  }

  /** The note for an aside over a parked delivery: the same reply-interruption note a cut reflex reply gets
   *  (interruptionNote), over the delivery as it was being said — the cut utterance's heard part, then every unheard
   *  piece. Nothing parked yet (the task is still working) → its result simply follows the answer. */
  private asideNote(told: Array<{ text: string; heard?: string }>): string {
    const rest = told.map((p) => p.text).join(' ').trim();
    if (!rest) return '\n[you were in the middle of delivering a task result — answer this briefly; the rest of the result follows right after your answer. If their words are an unfinished fragment, call Hold instead.]';
    const heard = told[0]?.heard ?? '';
    return interruptionNote({ full: heard + rest, heard }, 'aside');
  }

  /** Speak the parked delivery now (resume), in order, and clear it. */
  private redeliverParked(): void {
    const text = this.parkedRedeliver.splice(0).map((p) => p.text).join(' ').trim();
    if (!text) return;
    this.offerOpen = false;
    this.options.host?.notify?.({ kind: 'speak_utterance', message: text });
    this.notify('diag', 'parked_redelivered', { chars: text.length });
  }

  /** Resolve when all queued voice turns AND all in-flight worker tasks have settled (tests, graceful shutdown). */
  async idle(): Promise<void> {
    while (true) {
      const q = this.queue;
      await q.catch(() => {});
      await Promise.all([...this.tasks.values()].map((t) => t.promise));
      // a worker completion may have enqueued a re-voice turn meanwhile — loop until quiescent
      if (this.queue === q && ![...this.tasks.values()].some((t) => t.status === 'running')) return;
    }
  }

  /** Promise-chain mutex: turns run strictly one at a time; a failed turn doesn't poison the chain. */
  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.queue.then(fn, fn);
    this.queue = run.then(() => {}, () => {});
    return run;
  }

  private notify(kind: string, message: string, data?: unknown): void {
    this.emitHost({ kind, message, data });
  }

  /** Host-boundary emit for the reflex's spoken channel: during a PENDING speculation, text_delta and
   *  hold_filler are BUFFERED (nothing may reach TTS on unconfirmed input); confirm flushes them in
   *  order, abort drops them silently. Everything else (task_* lifecycle, worker speak_utterance —
   *  which bypasses this via host.notify directly) passes through untouched. */
  private emitHost(ev: { kind: string; message: string; data?: unknown }): void {
    const spec = this.spec;
    if (spec && (ev.kind === 'text_delta' || ev.kind === 'hold_filler')) {
      if (spec.state === 'pending') { spec.buf.push(ev); return; }
      if (spec.state === 'aborted') return; // an aborted speculation never speaks
    }
    this.options.host?.notify?.(ev);
  }

  /** Queue a `[task …]` event for re-voicing. Events arriving while the voice is busy coalesce into ONE turn.
   *  `nonClean` (out-of-band boolean, set by the caller that KNOWS this event integrates a NON-CLEAN outcome)
   *  marks the coalesced flush as a non-clean integration turn — turn-eligibility, never inferred from event
   *  text and never keyed on a (re-authored) brief string. Any dispatch in such a turn is a follow-up. */
  private queueRevoice(event: string, nonClean = false): void {
    this.pendingEvents.push(event);
    if (nonClean) this.pendingNonClean = true;
    if (this.flushQueued) return;
    this.flushQueued = true;
    void this.enqueue(async () => {
      this.flushQueued = false; // events landing during this send() get their own flush turn
      const events = this.pendingEvents.splice(0);
      const nonCleanTurn = this.pendingNonClean;
      this.pendingNonClean = false;
      if (!events.length) return;
      // A failed task MUST be surfaced to the user, never silently swallowed (the reflex
      // sometimes voices nothing on a [task … failed] event). Derive a spoken fallback so the
      // silent-turn repair announces the failure instead of a generic "could you say that again".
      const failed = events.find((e) => /^\[task\b[^\]\n]*\bfailed\b/i.test(e));
      this.resetTurn();
      // Turn-eligibility (NOT brief-matching): this flush integrates ≥1 non-clean task, so ANY dispatch it
      // makes is an auto-follow-up. This is what makes runaway impossible — a paraphrased escalation still
      // lands here and is recognized. resetTurn cleared the flag, so set it AFTER reset.
      this.turnFollowUp = nonCleanTurn;
      const res = await this.voice.send(events.join('\n'));
      await this.endTurn(res, failed ? "Sorry, that didn't work — the task failed." : undefined); // a re-voiced result that came back empty is dead air too

      // A re-voice turn ends OUTSIDE any host send() callsite — signal it so a line-buffered
      // renderer (CLI MarkdownStream) can flush its tail (else the last partial line is swallowed).
      this.notify('revoice_done', '');
    });
  }

  /** The worker's brief: the Act/Think args + a STATIC text snapshot of the recent conversation.
   *  Act briefs get a self-verify footer — the worker's report is trusted without review, so it
   *  must check its own work before reporting (nearly free under prompt caching; measured honest:
   *  it does NOT fix one-shot logic bugs — see mind/10). Think tasks are pure reasoning — no footer. */
  /** Static text snapshot of the last `turns` user/assistant messages (`role: text` lines). */
  recentExcerpt(turns = this.options.excerptTurns): string {
    return this.voice.transcript
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && contentText(m.content).trim())
      .slice(-turns)
      .map((m) => `${m.role}: ${contentText(m.content)}`)
      .join('\n');
  }

  private buildBrief(brief: string, tier: WorkerTier = 'act', deliver = true): string {
    const recent = this.recentExcerpt();
    const verify = tier === 'act'
      ? '\n\nBefore reporting done: re-read what you changed and check it against EVERY requirement above — fix any gap first. Your report is trusted without review.'
      : '';
    // DELIVER contract: the worker OWNS spoken delivery. Stated abstractly — NO concrete example
    // (this repo was burned by eval-fixture overfitting; teach the principle, not a fixture).
    const deliverContract = deliver ?
      '\n\n## DELIVER (spoken delivery)\n' +
      'You are reporting back to a user who is LISTENING. Stream your work normally — your prose is the written work record and detail, and is NOT spoken. ' +
      'Wrap anything the user should HEAR in <spoken>…</spoken> tags. ' +
      'LEAD WITH the actual content they asked for: if they asked for a specific piece of content — a value, a name, the actual lines, the writing itself — that content goes INSIDE the <spoken> tags, not a remark about it. ' +
      'Your FIRST <spoken> segment is substantive — never a greeting or an acknowledgement (the front-end has already acked; do not double-ack). ' +
      'Keep spoken text concise and natural for the ear: short sentences, no markdown. ' +
      (this.options.spokenBudgetChars ? `Speak a SHORT summary only: at most three short sentences with the one thing that matters most, then ONE short offer that names what else you have, as a question they can answer in a word — under ${this.options.spokenBudgetChars} characters in all, the offer included. Everything else stays in your written prose (it is kept and they can ask for it); spoken text past that budget is cut and held until they ask. ` : '') +
      'NEVER enumerate in speech — no numbered or bulleted lists ("One. … Two. …" is robotic). Deliver multiple items as flowing conversation with brief connective phrasing ("here\'s one…", "and another…", "oh, and…"), pausing between items with sentence breaks, not numbers.' +
      (this.options.emotionTags ? ' Inside <spoken>, you may prefix a sentence with an inline [emotion] tag (e.g. [excited], [curious]) to color how it is voiced — only when it genuinely fits, and vary it; [laughter] gives a natural laugh.' : '') : '';
    // Lead with fresh "now" so any search/computation is grounded to the current date — without it a
    // worker resolves "recent"/"latest" to a famous past instance (e.g. the 2022 World Cup instead of
    // the tournament happening this week). Recomputed here per dispatch, so it never goes stale.
    return `${nowLine()}.\n\n` + (recent ? `${brief}\n\n## Recent conversation (for context)\n${recent}` : brief) + verify + deliverContract;
  }

  /** Spawn a detached worker for task `id`; its settlement notifies + enqueues the re-voice turn. */
  private spawnWorker(id: string, label: string, briefText: string, tier: WorkerTier, brief: string, followUp: boolean): void {
    const o = this.options;
    const tierOpts = tier === 'think' ? o.thinkOptions : o.actOptions;
    const tierModel = tier === 'think' ? (o.thinkModel as string) : o.actModel;
    const controller = new AbortController();
    const base = tierOpts?.hooks ?? o.actOptions?.hooks;
    const report = o.progressUpdates ? this.progressReporter(id) : undefined;
    // Rolling activity tail (always on, unlike the gated progress re-voicer) — backs /tasks inspection.
    const tail: string[] = [];
    const pushTail = (line: string) => { tail.push(line.slice(0, 200)); if (tail.length > 120) tail.splice(0, tail.length - 120); };
    const hooks = {
      ...base,
      preToolUse: async (call: ToolUse, meta?: any) => { const d = await base?.preToolUse?.(call, meta); pushTail(`⚙ ${describeCall(call)}`); report?.pre(call); return d; },
      postToolUse: async (call: ToolUse, result: string, meta?: any) => {
        await base?.postToolUse?.(call, result, meta);
        const last = result?.trim().split('\n').filter(Boolean).pop();
        if (last) pushTail(`  ↳ ${last}`);
        report?.post(call);
      },
      onToolOutput: (call: ToolUse, chunk: string, meta?: any) => { base?.onToolOutput?.(call, chunk, meta); report?.output(chunk); },
    };
    const relayAsk = async (q: { question: string; options?: Array<{ label: string }> }): Promise<string> => {
      const opts = q.options?.length ? ` Options: ${q.options.map((x) => x.label).join(', ')}.` : '';
      const a = await this.parkQuestion(id, `${q.question}${opts}`);
      return a || '(no answer from the user — use your best judgment and note the assumption)';
    };
    // The worker OWNS delivery: it streams `<spoken>…</spoken>` during its run. Split each text_delta —
    // spoken segments speak as a SEPARATE serialized utterance (speak_utterance) AND record into the
    // transcript/surfaced text (INDEPENDENTLY of whether the audio queue plays them — a barge-in can
    // drop the audio, but the spoken content must still land in context). detail → activity tail, never
    // spoken. `stream:true` is only meaningful with a host.notify (Agent requires it).
    const splitter = new SpokenSplitter();
    // Suppress audio once the user has barged in on this task (deliveryParked) — don't talk over the new
    // topic. The result still folds into the transcript at settle, so it's recoverable, just not spoken.
    const speak = (seg: string) => this.deliverSegment(this.tasks.get(id), seg);
    // Coalesce spoken text into COMPLETE sentences before emitting each as its own utterance — a worker on
    // a fragmenting model (cursor streams word-by-word deltas) would otherwise emit one utterance per word
    // (choppy TTS + one screen-echo line per word). flushSpoken() drains the partial when the stream ends.
    const coalescer = new SentenceCoalescer();
    const feedSpoken = (s: string) => { const ready = coalescer.feed(s); if (ready) speak(ready); };
    const flushSpoken = () => speak(coalescer.flush());
    const askBridge = o.askRelay ? { ask: relayAsk } : o.host?.ask ? { ask: (q: any) => o.host!.ask!(q) } : {};
    const workerHost: HostBridge = {
      ...askBridge,
      notify: (ev) => {
        if (ev?.kind === 'text_delta' && typeof (ev as any).message === 'string') {
          const { spoken, detail } = splitter.feed((ev as any).message);
          feedSpoken(spoken);
          // Out of the block: nothing more of it is coming — release a sentence held on a possible decimal ("…in 2026.").
          if (!splitter.inSpoken) flushSpoken();
          if (detail.trim()) pushTail(detail.trim());
          return;
        }
        // non-stream worker events stay on the activity tail (never spoken)
      },
    };
    const agentOpts: Partial<AgentOptions> = {
      ai: o.ai,
      fs: o.fs,
      model: tierModel,
      ...(tier === 'think' ? { reasoning: tierOpts?.reasoning ?? 'high' } : {}),
      ...tierOpts,
      // Recompute providerOptions for THIS worker's model (after tierOpts so it wins over any inherited
      // main-template value) — prevents cursor-only cwd/cursorSession leaking onto an anthropic worker.
      providerOptions: o.providerOptionsFor?.(tierModel),
      stream: true, // worker streams text_delta so the splitter can extract <spoken> live (after tierOpts: never overridden off)
      host: workerHost, // carries BOTH ask AND the <spoken>-splitting notify
      ...(hooks ? { hooks } : {}),
      signal: controller.signal, // shared with the checker so a cancel tears down both
    };
    const promise = new Agent(agentOpts)
      .run(briefText)
      // Worker stream done: drain any dangling spoken text (unclosed <spoken> + the last partial sentence).
      .then((res) => { const { spoken, detail } = splitter.flush(); feedSpoken(spoken); if (detail.trim()) pushTail(detail.trim()); flushSpoken(); return res; })
      .then((res) => this.maybeVerify(id, brief, res, tier, agentOpts, askBridge))
      .then((res) => this.onWorkerSettled(id, res))
      .catch((err) => this.onWorkerFailed(id, err));
    this.tasks.set(id, { id, label, status: 'running', controller, promise, tail, brief, followUp, splitter, createdTurn: this.turn });
    // Evict oldest settled records past the cap (Map preserves insertion order) — running tasks stay.
    if (this.tasks.size > this.options.maxTaskRecords)
      for (const [tid, rec] of this.tasks) {
        if (this.tasks.size <= this.options.maxTaskRecords) break;
        if (rec.status !== 'running') this.tasks.delete(tid);
      }
  }

  /** Fresh-context check of a successful Act task: a NEW agent (same model/fs/tools, but NO shared
   *  conversation context) re-reads the file state against the brief and fixes any gap. The fix lands
   *  on the shared fs automatically (workers write fs directly, no overlay), so grading sees the
   *  corrected state. Bounded to ONE pass. Off unless `verifyActTasks`; never runs for think/failed/
   *  cancelled tasks. Usage is merged so /cost reflects the real (worker + checker) spend. */
  private async maybeVerify(id: string, brief: string, res: RunResult, tier: WorkerTier, agentOpts: Partial<AgentOptions>, askBridge: Partial<HostBridge>): Promise<RunResult> {
    if (!this.options.verifyActTasks || tier !== 'act' || res.finishReason !== 'stop') return res;
    if (this.tasks.get(id)?.status === 'cancelled') return res; // user stopped it — don't spend on a check
    // The checker is an INTERNAL fixer — it must NOT speak and must NOT touch the worker's SpokenSplitter.
    // So: (a) strip the DELIVER/<spoken> contract from the brief, and (b) run with a clean host that
    // carries ONLY `ask` (the historical pre-spoken-feature behavior) — never the spoken-forwarding
    // notify/splitter — and without `stream:true` (only meaningful for live <spoken> extraction).
    const { stream: _stream, host: _host, ...restOpts } = agentOpts;
    const checkerOpts: Partial<AgentOptions> = {
      ...restOpts,
      ...(askBridge.ask ? { host: { ask: askBridge.ask } } : {}),
    };
    const checkBrief = `${this.buildBrief(brief, tier, false)}\n\n## VERIFY MODE\nAnother agent just implemented the above. Independently check the CURRENT state of the files against EVERY requirement. Fix any gap you find. If everything is already correct, make NO changes — do not refactor or improve — and report "verified".`;
    this.notify('task_verify', `task ${id}: verifying`, { id });
    const cres = await new Agent(checkerOpts).run(checkBrief);
    // Surface an inconclusive check (errored / hit a kill-switch) — don't let a failed verification
    // masquerade as a passed one. The worker's work still stands, so the task still completes with
    // the worker's report; we just flag that the check didn't cleanly finish.
    if (cres.finishReason !== 'stop') {
      log.warn(`task ${id}: verify inconclusive (${cres.finishReason})`);
      this.notify('task_verify', `task ${id}: verify inconclusive (${cres.finishReason})`, { id, finishReason: cres.finishReason });
    }
    const sum = (a = 0, b = 0) => a + b;
    return {
      ...res,
      steps: res.steps + cres.steps,
      // Merge the checker's messages so downstream tool-call/step accounting includes BOTH agents
      // (else a verified task's toolCalls would undercount vs its steps/usage).
      messages: [...res.messages, ...cres.messages],
      usageEstimated: res.usageEstimated || cres.usageEstimated,
      usage: res.usage && cres.usage ? {
        promptTokens: sum(res.usage.promptTokens, cres.usage.promptTokens),
        completionTokens: sum(res.usage.completionTokens, cres.usage.completionTokens),
        totalTokens: sum(res.usage.totalTokens, cres.usage.totalTokens),
        cacheCreationTokens: sum(res.usage.cacheCreationTokens, cres.usage.cacheCreationTokens),
        cacheReadTokens: sum(res.usage.cacheReadTokens, cres.usage.cacheReadTokens),
      } : (res.usage ?? cres.usage),
    };
  }

  /** Throttled per-task progress: worker tool calls → at most one progress re-voice per interval.
   *  Two sources, one throttle: completed steps (post) and a heartbeat for a SINGLE long tool call
   *  (pre records the in-flight call; a self-cleaning timer narrates "still inside Bash — 70s").
   *  Completion supersedes: nothing is emitted once the task has settled. */
  private progressReporter(id: string): { pre: (call: ToolUse) => void; post: (call: ToolUse) => void; output: (chunk: string) => void } {
    let lastAt = Date.now();
    let steps = 0;
    let inflight: { call: ToolUse; at: number; tail: string } | null = null;
    const due = () => {
      if (this.pendingAsks.size) return undefined; // a question is pending — "still working" asides are noise (and wrong: it's WAITING)
      const rec = this.tasks.get(id);
      // a parked task (barged, or moved on from) must not narrate over the user's new topic
      return rec && rec.status === 'running' && !rec.deliveryParked && Date.now() - lastAt >= this.options.progressIntervalMs ? rec : undefined;
    };
    const emit = (rec: TaskRecord, line: string, call: ToolUse) => {
      lastAt = Date.now();
      this.notify('task_progress', `task ${id} (${rec.label}): ${line}`, { id, steps, call: call.name });
      this.queueRevoice(`[task ${id} progress] ${line}`);
    };
    const timer = setInterval(() => {
      const rec = this.tasks.get(id);
      if (!rec || rec.status !== 'running') return clearInterval(timer); // task settled — self-clean
      if (!inflight || !due()) return;
      // content-aware when the tool streams (ctx.emit → rolling tail): "last output: 12 pass, 0 fail"
      const last = inflight.tail.trim().split('\n').filter(Boolean).pop()?.slice(-80);
      emit(rec, `still inside ${describeCall(inflight.call)} — ${Math.round((Date.now() - inflight.at) / 1000)}s on this step${last ? `, last output: ${last}` : ''}`, inflight.call);
    }, Math.max(this.options.progressIntervalMs, 250));
    (timer as any).unref?.(); // never hold the process open
    return {
      pre: (call) => { inflight = { call, at: Date.now(), tail: '' }; },
      output: (chunk) => { if (inflight) inflight.tail = (inflight.tail + chunk).slice(-500); }, // digest only — NEVER re-voices directly
      post: (call) => {
        steps++;
        inflight = null;
        const rec = due();
        if (rec) emit(rec, `still running — ${steps} steps so far, now: ${describeCall(call)}`, call);
      },
    };
  }

  /** Park a question under `askId` (a task id, or any unique key for permission asks): re-voices
   *  '[task <id> asks] …' and resolves with the user's answer via AnswerTask — or '' on timeout/
   *  task settle (callers map '' to deny / best-judgment). Workers never block forever. */
  parkQuestion(askId: string, question: string): Promise<string> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (answer: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.pendingAsks.delete(askId);
        resolve(answer);
      };
      const timer = setTimeout(() => {
        this.notify('task_ask_timeout', `task ${askId}: question timed out — proceeding without an answer`);
        finish('');
      }, this.options.askTimeoutMs);
      this.pendingAsks.set(askId, { question, resolve: finish });
      this.notify('task_ask', `task ${askId} asks: ${question}`, { id: askId, question });
      this.queueRevoice(`[task ${askId} asks] ${question}\n(Relay this to the user in your own words. When they answer, call AnswerTask with id "${askId}" and their answer.)`);
    });
  }

  /** Resolve any question a settling/cancelled task left parked (its answer can no longer matter). */
  private dropAsk(id: string): void {
    this.pendingAsks.get(id)?.resolve('');
  }

  /** Build the INTEGRATION TURN prompt for a NON-CLEAN settled worker (early stop / failure). A clean
   *  success never reaches here — it streams its own `<spoken>` delivery during the run. For a partial
   *  or failed result the outcome re-enters the reflex as a decision (like a tool_result flowing back
   *  into a normal agent loop): the reflex evaluates the outcome against the original intent and chooses
   *  what to do next.
   *
   *  Decision branches (the reflex acts on them with EXISTING tools — no new surface):
   *    • accept     → SPEAK the (partial) result plainly — don't dress a failure up as success.
   *    • escalate   → call `Think` with the SAME brief — only when Act failed/stalled AND a Think tier
   *                   exists AND this task wasn't already a follow-up (one hop max). Wires the dead
   *                   "Reserve Think for a problem Act already FAILED at" promise.
   *    • re-delegate→ call `Act` with a CORRECTED brief — for a recoverable error / partial result.
   *    • ask        → ask the user ONE concrete question if genuinely blocked.
   *
   *  Keeps the `[task <id> completed]` / `[task <id> failed]` opener so existing coalescing + the
   *  failed-revoice fallback still fire, and the per-event transcript markers stay intact. */
  private integrationPrompt(rec: TaskRecord, outcome: 'incomplete' | 'error', body: string, finishReason: string): string {
    const opener = outcome === 'error' ? `[task ${rec.id} failed]` : `[task ${rec.id} completed]`;
    // Non-clean (early stop or error): give the reflex the decision. Cap auto-follow-up to one hop.
    // Hard absolute backstop ([[property 4]]): once the per-conversation auto-escalation budget is spent,
    // never offer escalate/re-delegate again — caps total escalations even if attribution ever misfires.
    const underCap = this.autoEscalations < DuplexAgent.MAX_AUTO_ESCALATIONS;
    const canEscalate = (outcome === 'error' || outcome === 'incomplete') && underCap;
    const hasThink = this.options.thinkModel !== false;
    const options: string[] = [];
    if (!rec.followUp && canEscalate && hasThink)
      options.push('ESCALATE to the Think tier (call Think with the same brief) if this is a hard/architectural problem the Act worker stalled or failed on');
    if (!rec.followUp && canEscalate)
      options.push('RE-DELEGATE to Act with a corrected brief if the failure looks recoverable (a wrong path, a fixable mistake)');
    options.push('ASK the user one short, concrete question if you genuinely cannot proceed without their input');
    options.push("ACCEPT and tell the user plainly what happened (don't dress a failure up as success)");
    const decision = options.length > 1
      ? ` You must decide what to do next — choose ONE: ${options.map((o, i) => `(${i + 1}) ${o}`).join('; ')}. Pick exactly one and act on it; do not voice this as a finished success.`
      : ` Tell the user plainly what happened — do not present this as a finished success.`;
    const state = outcome === 'error'
      ? `the worker FAILED with: ${body}`
      : `the worker STOPPED EARLY (${finishReason}) — its result is PARTIAL, not a finished success: ${body}`;
    return `${opener} Original request: "${rec.brief}". Outcome: ${state}.${decision}`;
  }

  private onWorkerSettled(id: string, res: RunResult): void {
    this.dropAsk(id);
    const rec = this.tasks.get(id)!;
    if (res.finishReason === 'aborted' || rec.status === 'cancelled') {
      rec.status = 'cancelled';
      this.notify('task_cancelled', `task ${id} (${rec.label}) cancelled`);
      return; // the user asked to stop — don't narrate cancelled work
    }
    if (res.finishReason === 'error') {
      const msg = res.error instanceof Error ? res.error.message : String(res.error ?? 'unknown error');
      return this.failTask(rec, msg);
    }
    rec.status = 'done';
    rec.result = res.text;
    // A non-'stop' finish that isn't error/aborted (max_steps, timeout, loop, budget, max_tool_calls)
    // means the worker STOPPED EARLY — its result is partial, NOT a clean success. It must not be voiced
    // as if complete; the integration turn is told so it can escalate/re-delegate instead of accepting.
    // (`needs_input` also lands here, but only in a degenerate piped duplex: normally a worker always
    // has an `ask` — the voice relay, else the host's — so its question reaches the user as speech.)
    // `empty` (the Agent's own zero-work guard, 0cff9da) is NOT partial work stopped early — by
    // definition it's zero text + zero tools, i.e. the worker said nothing at all. That is exactly the
    // "nothing to report" case this duplex contract already treats as an acceptable clean finish (below);
    // routing it into the incomplete/re-voice path would pollute the transcript with a [task…] escalation
    // over silence instead of just letting the empty-result branch handle it.
    const incomplete = res.finishReason !== 'stop' && res.finishReason !== 'empty';
    log.verbose(`task ${id} done (${res.steps} steps${incomplete ? `, INCOMPLETE: ${res.finishReason}` : ''})`);
    // toolCalls = the worker's REAL tool-call count (a dispatch is not a tool call — consumers
    // comparing efficiency across agents need the worker's actual work, not the escalation count).
    this.notify('task_done', `task ${id} (${rec.label}) completed`, {
      id, text: res.text, usage: res.usage, usageEstimated: res.usageEstimated,
      finishReason: res.finishReason,
      steps: res.steps, toolCalls: res.messages.filter((m) => m.role === 'tool').length,
    });
    if (incomplete) {
      // A SUPERSEDED delivery (user moved to new work) must not hijack the voice on a partial result, nor
      // auto-escalate abandoned work over the new topic — fold the partial into the transcript (recoverable)
      // and stop, matching the clean-success suppression below.
      if (this.foldIfSuperseded(rec, res.text)) return;
      this.dropParked(rec);
      // Early stop → PARTIAL: keep the existing reflex revoice/decision path (escalate/re-delegate/ask).
      return this.queueRevoice(this.integrationPrompt(rec, 'incomplete', res.text, res.finishReason), true);
    }
    // Clean success: the worker ALREADY spoke its <spoken> segments during its run (no reflex re-voicing).
    // Parked (user barged in mid-task): suppress the remaining SPOKEN delivery — but still fold the full
    // result into the transcript so it's not lost (the reflex can surface it if the user asks).
    const tail = rec.splitter?.flush(); // drain any buffered partial tag (held across the last delta)
    if (tail?.spoken) this.deliverSegment(rec, tail.spoken);
    // (a) No-spoken fallback: the worker emitted ZERO spoken segments → delivery would be silent. Deliver the result
    //     as one segment (spoken, or parked for a resume if the user barged). A parked task's spoken segments were
    //     already parked one by one as they came (deliverSegment) — nothing is re-collected here, so nothing repeats.
    if (!rec.splitter?.spokeAny && res.text.trim()) this.deliverSegment(rec, res.text);
    // (b) Fold the FULL result into the transcript as an assistant turn so the reflex can answer
    //     follow-ups from context (replaces what voice.send did implicitly on the old revoice path) — with the budget
    //     offer if one was spoken: it was the last question the user heard, so a reply answers IT, not the written one.
    if (res.text.trim()) this.voice.transcript.push({ role: 'assistant', content: res.text + (rec.capOffer ? `\n\n(Said aloud in place of the rest of the spoken summary: "${rec.capOffer}")` : '') });
  }

  private onWorkerFailed(id: string, err: unknown): void {
    this.failTask(this.tasks.get(id)!, err instanceof Error ? err.message : String(err));
  }

  /** A delivery the user has SUPERSEDED — moved on from (deliveryParked 'moved_on', set by a redirect) —
   *  must not hijack the voice when it settles late (partial or failed): that would talk over the new topic
   *  and, worse, auto-escalate work the user already abandoned. Fold a recoverable note into the transcript
   *  instead (the reflex can surface it if asked) and report true so the caller skips queueRevoice. A merely
   *  barge-parked task ('barge' — a "go on" may still resume it) is NOT superseded: it
   *  keeps the normal surfacing path so a failure the user still cares about is not swallowed. */
  private foldIfSuperseded(rec: TaskRecord, note: string): boolean {
    if (rec.deliveryParked !== 'moved_on') return false;
    if (note.trim()) this.voice.transcript.push({ role: 'assistant', content: note });
    log.verbose(`task ${rec.id} settled but superseded (${rec.status}) — folded into transcript, not voiced`);
    return true;
  }

  private failTask(rec: TaskRecord, msg: string): void {
    this.dropAsk(rec.id);
    rec.status = 'error';
    rec.result = msg;
    log.warn(`task ${rec.id} failed: ${msg}`);
    this.notify('task_error', `task ${rec.id} (${rec.label}) failed: ${msg}`);
    // Superseded (user moved on): fold a note, don't hijack the new topic with a failure/escalation.
    if (this.foldIfSuperseded(rec, `The "${rec.label}" task failed: ${msg}`)) return;
    this.dropParked(rec);
    this.queueRevoice(this.integrationPrompt(rec, 'error', msg, 'error'), true);
  }

  // --- voice tools (closures over this instance) ---

  /** Live-switch the think tier: `false` disables (removes the Think tool from the voice agent),
   *  a model id enables (adds the tool if missing). The system-prompt THINK_SLOT text is frozen at
   *  construction — the tool's own description carries the routing guidance, so a live enable works;
   *  dispatch()'s think→act fallback covers any straggler calls after a live disable. */
  setThinkModel(model: string | false): void {
    this.options.thinkModel = model;
    const tools = this.voice.options.tools as AgentTool[];
    const i = tools.findIndex((t) => t.name === 'Think');
    if (model === false && i >= 0) tools.splice(i, 1);
    else if (model !== false && i < 0) tools.push(this.thinkTool());
  }

  /** User/programmatic spawn: the CLI's /act and /think commands. Returns the task id.
   *  `followUp` marks an automatic escalation/re-delegation (set by the integration turn) so the new
   *  task's own integration turn won't escalate again — capping auto-follow-ups to one hop. */
  async dispatch(brief: string, tier: WorkerTier = 'act', label?: string, followUp = false): Promise<string> {
    if (tier === 'think' && this.options.thinkModel === false) tier = 'act';
    if (followUp) this.autoEscalations++; // spend from the hard absolute escalation budget ([[property 4]])
    // A new dispatch = the user redirected to fresh actionable work. Park deliveries still running from a
    // PRIOR turn so a superseded worker can't speak its <spoken> over the new topic when it settles (session
    // 1d93c6d3: a mis-heard search talked over the corrected one). Prior-turn only (createdTurn < this.turn),
    // so parallel tasks dispatched in the SAME turn don't silence each other, and a pure conversational turn
    // that dispatches nothing leaves running deliveries intact. reenableResume:false keeps the superseded
    // delivery suppressed rather than resurfacing on a stray "go on".
    this.parkInFlightDeliveries(false, true);
    const id = `t${++this.seq}`;
    const lbl = label ?? tier;
    await this.options.onTaskStart?.(id, lbl);
    this.spawnWorker(id, lbl, this.buildBrief(brief, tier), tier, brief, followUp);
    this.notify('task_started', `task ${id} (${lbl}) started`, { id, brief, tier });
    return id;
  }

  /** Tool result for a dispatch. The task is ALREADY RUNNING — live, the terse "Acknowledge briefly" drew
   *  "Did you want me to look up today's news?" right after dispatching exactly that (session 20260923-101910
   *  msg 18). Also truthful about delivery: a clean result is spoken by the worker itself and lands in
   *  context (TaskStatus returns it); only failures come back as a [task … failed] event. */
  private dispatchResult(id: string, verb: string): string {
    const after = `The worker speaks its result to the user itself when done (TaskStatus ${id} returns it).`;
    return this.spokeBeforeDispatch
      ? `${verb} task ${id} — it is already running. You already acknowledged — end your turn now with NO further text. ${after}`
      : `${verb} task ${id} — it is already running. Now tell the user in ONE short sentence that you are on it (what you are doing) — do NOT ask whether they want it; it is already happening. ${after}`;
  }

  private actTool(): AgentTool {
    return {
      name: 'Act',
      description:
        'Escalate real work (reading/editing files, searching, running tasks, building) to a standard background worker. ' +
        'Returns immediately with a task id; when it finishes the worker speaks its result to the user itself (failures arrive as a "[task <id> failed]" event). ' +
        'Provide a clear, self-contained `brief` (the worker does not hear the live conversation).',
      parameters: {
        type: 'object',
        required: ['brief'],
        properties: {
          brief: { type: 'string', description: 'full, self-contained instructions for the worker' },
          label: { type: 'string', description: 'a short (2-4 word) label for the task' },
        },
      },
      run: async ({ brief, label }) => {
        this.spokeBeforeDispatch = this.spokeThisTurn; // speech before the dispatch WAS the ack — don't stack another
        this.turnDispatched = true;
        this.turnBriefs.add(String(brief ?? ''));
        // Turn-terminal: force the NEXT reflex step to be text-only (the spoken ack), so a weak model
        // can't keep polling TaskStatus and emit a redundant ack each step. Cleared by resetTurn.
        this.voice.options.toolChoice = 'none';
        const id = await this.dispatch(String(brief ?? ''), 'act', label ? String(label) : undefined, this.turnFollowUp);
        return this.dispatchResult(id, 'Acting on');
      },
    };
  }

  private thinkTool(): AgentTool {
    return {
      name: 'Think',
      description:
        'Escalate to a premium deep-reasoning agent for complex analysis, architecture decisions, hard debugging, or planning. ' +
        'Same async pattern as Act — returns a task id. Use when the problem needs careful thought before (or instead of) action. ' +
        'Do not use Think for simple tasks — Act is cheaper and faster.',
      parameters: {
        type: 'object',
        required: ['brief'],
        properties: {
          brief: { type: 'string', description: 'the question or problem to reason about deeply' },
          label: { type: 'string', description: 'a short (2-4 word) label for the task' },
        },
      },
      run: async ({ brief, label }) => {
        this.spokeBeforeDispatch = this.spokeThisTurn; // speech before the dispatch WAS the ack — don't stack another
        this.turnDispatched = true;
        this.turnBriefs.add(String(brief ?? ''));
        // Turn-terminal: force the NEXT reflex step to be text-only (the spoken ack), so a weak model
        // can't keep polling TaskStatus and emit a redundant ack each step. Cleared by resetTurn.
        this.voice.options.toolChoice = 'none';
        const id = await this.dispatch(String(brief ?? ''), 'think', label ? String(label) : undefined, this.turnFollowUp);
        return this.dispatchResult(id, 'Thinking on');
      },
    };
  }

  private taskStatusTool(): AgentTool {
    return {
      name: 'TaskStatus',
      description: 'Status of background tasks. Pass `id` for one task, or omit it to list all.',
      parameters: { type: 'object', properties: { id: { type: 'string' } } },
      run: async ({ id }) => {
        const list = id ? [this.tasks.get(String(id))].filter(Boolean) as TaskRecord[] : [...this.tasks.values()];
        if (!list.length) return id ? `No task '${id}'.` : 'No background tasks.';
        // A settled task carries what it FOUND — a bare "done" left the reflex unable to answer "what did
        // it find?" (live: it deliberated aloud instead, session 20260923-101910 msg 33). Prefer the worker's
        // spoken delivery (already ear-shaped), else its report; bounded so a lookup can't bloat the context.
        const cap = id ? 1500 : 300;
        const clip = (x: string) => (x.length > cap ? `${x.slice(0, cap)}… (truncated)` : x);
        return list.map((t) => {
          const head = `${t.id} (${t.label}): ${t.status}`;
          const body = (t.status === 'done' ? t.spokenText?.trim() || t.result?.trim() : t.status === 'error' ? t.result?.trim() : '') ?? '';
          return body ? `${head}. ${t.status === 'error' ? 'Error' : 'Result'}: ${clip(body)}` : head;
        }).join('\n');
      },
    };
  }

  /** Sub-100ms read-only lookups the voice may do itself — everything else stays Act-only.
   *  fs-only (no shell; the engine is VFS-abstracted): time, git branch (.git/HEAD read), ls, file
   *  head. Output is hard-capped so a lookup can never bloat the skinny voice context. */
  private quickLookTool(): AgentTool {
    const CAP = 2000; // chars — a "peek", not a Read
    const kinds = [...new Set(['time', 'branch', 'ls', 'file', 'capabilities', ...Object.keys(this.options.quickLook ?? {})])];
    return {
      name: 'QuickLook',
      description:
        `Instant read-only lookup — one of: ${kinds.join(', ')}. ` +
        (kinds.includes('sessions') ? '`sessions` = EARLIER conversations with this user: what:"sessions" lists them; what:"sessions" with path:"<session id>" reads one (not what:"file"). ' : '') +
        'For trivial facts only; anything needing search, commands, or reasoning goes through Act.',
      parameters: {
        type: 'object',
        required: ['what'],
        properties: {
          what: { type: 'string', enum: kinds, description: 'what to look up' },
          path: { type: 'string', description: 'for ls/file: the path to look at; for sessions: a session id (or "previous")' },
        },
      },
      run: async ({ what, path }) => {
        const fs = this.options.fs;
        try {
          // A session id asked for as a file/folder (live 20260923-192543: what:"file" path:"20260923-185437-test",
          // three times, each a "not found" + cwd listing) is a session read — route it.
          const sessions = this.options.quickLook?.sessions;
          if (sessions && (what === 'file' || what === 'ls') && SESSION_ID_RE.test(String(path ?? '').trim())) return await sessions(String(path).trim());
          const over = this.options.quickLook?.[String(what)];
          if (over) return await over(path ? String(path) : undefined);
          switch (String(what)) {
            case 'capabilities': {
              // CC-style self-introspection: report the worker's REAL tools so the reflex can
              // answer "can you…?" by looking, not guessing. Generalized — no capability hard-coded.
              const actTools = (this.options.actOptions?.tools ?? []) as AgentTool[];
              const names = actTools.map((t) => t.name);
              // MCP servers reach an autonomous worker (cursor/*) via providerOptions, NOT as actTools —
              // so without this the reflex underreports (e.g. denies browser control it actually has).
              const mcpServers = Object.keys((this.options.actOptions?.providerOptions as any)?.mcpServers ?? {});
              const mcpNote = mcpServers.length
                ? ` Plus MCP servers your worker can use: ${mcpServers.join(', ')} (e.g. browser-bridge → drive a real browser: open tabs, navigate, click, screenshot).`
                : '';
              if (!names.length)
                return 'Your worker uses Act\'s default local toolset (reading/editing files, running shell ' +
                  'commands). No extra tools (e.g. web/internet) are configured; if a request is not a ' +
                  'basic file or shell operation, assume you can\'t do it and say so.' + mcpNote;
              // Each tool stands on its own — the reflex must resolve a request to the RIGHT one.
              // The trap: it conflates three distinct web capabilities. Spell them apart so it neither
              // overclaims (WebFetch ≠ search) nor underclaims (a browser CAN reach a search engine).
              const hasFetch = names.some((n) => /WebFetch/i.test(n));
              const hasBrowser = names.some((n) => /browser.*(navigate|click|page|type)/i.test(n));
              const hasSearch = names.some((n) => /(^|_)WebSearch$|search/i.test(n) && !/WebFetch|browser/i.test(n));
              const notes: string[] = [];
              if (hasFetch) notes.push('WebFetch retrieves ONE specific URL you are given — it is not a search engine.');
              if (hasBrowser) notes.push('The browser tools drive a real browser: you CAN open a site and, if needed, navigate to a search engine and search there — but it is manual and takes a moment, not an instant lookup.');
              else if (!hasSearch && hasFetch) notes.push('You have no general web-search tool, so for an instant "search the web" you can only fetch a URL they provide.');
              const webNote = notes.length ? ' NOTE: ' + notes.join(' ') : '';
              return `Tools your background worker (Act) can actually use: ${names.join(', ')}. ` +
                'Read each name literally and match the request to a SPECIFIC tool; if none fits, you do NOT have ' +
                'that ability — say so honestly.' + webNote + mcpNote;
            }
            case 'time':
              return new Date().toString();
            case 'branch': {
              if (!fs) return 'unavailable (no filesystem)';
              const head = (await fs.readFile('.git/HEAD')).trim();
              return head.startsWith('ref: refs/heads/') ? `branch: ${head.slice('ref: refs/heads/'.length)}` : `detached HEAD at ${head.slice(0, 12)}`;
            }
            case 'ls': {
              if (!fs) return 'unavailable (no filesystem)';
              const p = String(path ?? '.');
              try {
                const names = await fs.readDir(p);
                return names.slice(0, 50).join('\n') + (names.length > 50 ? `\n… (+${names.length - 50} more)` : '');
              } catch {
                // The reflex often passes the cwd's OWN name ("linkedin" while already in …/linkedin →
                // …/linkedin/linkedin, not found). Never fail the turn (live: the reflex then asked the
                // user for a path — bad UX) — fall back to the cwd listing with a note.
                const names = await fs.readDir('.').catch(() => [] as string[]);
                return `'${p}' not found here — you are likely already inside it. Current directory listing:\n` +
                  names.slice(0, 50).join('\n') + (names.length > 50 ? `\n… (+${names.length - 50} more)` : '');
              }
            }
            case 'file': {
              if (!fs) return 'unavailable (no filesystem)';
              if (!path) return 'file lookup needs a path';
              try {
                const text = await fs.readFile(String(path));
                return text.length > CAP ? text.slice(0, CAP) + `\n… (truncated — ${text.length} chars total; Act for the full file)` : text;
              } catch {
                const names = await fs.readDir('.').catch(() => [] as string[]);
                return `'${path}' not found. Current directory contains:\n` + names.slice(0, 50).join('\n');
              }
            }
            default:
              return `unknown lookup '${what}'`;
          }
        } catch (e: any) {
          return `lookup failed: ${e?.message ?? e}`;
        }
      },
    };
  }

  private answerTaskTool(): AgentTool {
    return {
      name: 'AnswerTask',
      description: 'Relay the user\'s answer to a pending question from a background task (the "[task <id> asks]" events). Pass the id from the event and the user\'s answer.',
      parameters: {
        type: 'object',
        required: ['id', 'answer'],
        properties: { id: { type: 'string' }, answer: { type: 'string', description: "the user's answer, verbatim or faithfully summarized" } },
      },
      run: async ({ id, answer }) => {
        const ask = this.pendingAsks.get(String(id));
        if (!ask) return `No pending question for '${id}' — it may have been answered already or timed out.`;
        ask.resolve(String(answer ?? ''));
        return `Answer relayed — task ${id} resumes.`;
      },
    };
  }

  private holdTool(): AgentTool {
    return {
      name: 'Hold',
      description:
        'The user seems mid-thought — hold the turn (stay listening) instead of answering. ' +
        'Optionally pass a short filler ("mhm", "go on") to speak while waiting. Use when the ' +
        'message sounds incomplete, trailing off, or like they paused to think.',
      parameters: {
        type: 'object',
        properties: {
          filler: { type: 'string', description: 'optional short filler to speak ("mhm", "go on", "mm-hm")' },
        },
      },
      run: async ({ filler }) => {
        this.heldThisTurn = true; // enforce silence mechanically — the weak reflex ignores "do not respond" and emits "…"
        // Always notify (even with no filler): the host must cancel any pending adaptive micro-ack —
        // a Hold turn is INTENTIONALLY silent, an ack would read as the start of an answer.
        this.notify('hold_filler', filler ? String(filler) : '');
        return 'Holding — listening for the rest of the user\'s thought. Do not respond further this turn.';
      },
    };
  }

  private cancelTaskTool(): AgentTool {
    return {
      name: 'CancelTask',
      description: 'Cancel a running background task by id.',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      run: async ({ id }) => this.cancelTask(String(id)),
    };
  }
}
